import * as THREE from "three";
import { GIT_BRANCH } from "../generated/git_branch.js";
import {
  DebugSettings,
  GlobalSettings,
  RuntimeEnvironmentKey,
  getNextRuntimeEnvironmentKey,
  getRuntimeEnvironmentLabel,
  getServerRoutingOverride,
  setRuntimeEnvironmentOverride,
  setServerRoutingOverride,
  setUiBackendOverride,
  UI_BACKEND_OVERRIDE_KEY,
} from "../global_settings.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { ServerRoutingService } from "../server_routing/server_routing_service.js";
import { DailyChallengeService } from "../daily_challenge/daily_challenge_service.js";
import { DailyTasksService } from "../daily_tasks/daily_tasks_service.js";
import { WinStreakTowerService } from "../win_streak_tower/win_streak_tower_service.js";
import { StressTest } from "./stress_test.js";
import { StressRenderHud } from "./stress_render_hud.js";
import { BrickBench } from "./brick_bench.js";
import { BlockShowcase } from "./block_showcase.js";
import { DiamondTowerShowcase } from "./diamond_tower_showcase.js";
import { JarPyramidShowcase } from "./jar_pyramid_showcase.js";
import { TiltedTableShowcase } from "./tilted_table_showcase.js";
import { RotatedTableShowcase } from "./rotated_table_showcase.js";
import { ShelfTableShowcase } from "./shelf_table_showcase.js";
import { HangingTableShowcase } from "./hanging_table_showcase.js";
import { ElevatorTableShowcase } from "./elevator_table_showcase.js";
import { SlideTableShowcase } from "./slide_table_showcase.js";
import { TableCaseShowcase } from "./table_case_showcase.js";
import { LeaningLogsShowcase } from "./leaning_logs_showcase.js";
import { JarCasesShowcase } from "./jar_cases_showcase.js";
import { JarSandwichShowcase } from "./jar_sandwich_showcase.js";
import { JarBayShowcase } from "./jar_bay_showcase.js";
import { TextureTest } from "./texture_test.js";
import { TableFormationShowcase } from "./table_formation_showcase.js";
import { NewBlockModelsShowcase } from "./new_block_models_showcase.js";
import { BrickLevelsShowcase } from "./brick_levels_showcase.js";
import { StickyMineShowcase } from "./sticky_mine_showcase.js";
import { BarrierShowcase } from "./barrier_showcase.js";
import { LevelPoolBrowser } from "./level_pool_browser.js";
import { TriangleBench } from "./triangle_bench.js";
import { RimLightTest } from "./rim_light_test.js";
import { FpsOverlay } from "./fps_overlay.js";
import { VfxBlockShatterTask } from "../vfx_3d/effects/block_shatter/vfx_block_shatter_task.js";
import { GameScreen } from "../ui/screens/game_screen/game_screen.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { Game } from "../game.js";
import { Vector2 } from "../common/vector2.js";
import { MemoryOverlay } from "./memory_overlay.js";
import { MemoryDashboard } from "./memory_dashboard.js";
import { RoyalWheelDebug } from "./royal_wheel_debug.js";
import { LogOverlay } from "./log_overlay.js";
import { UiPerfProbe } from "./ui_perf_probe.js";
import { PerfSession } from "./perf_session.js";
import { MenuScreen } from "../ui/screens/menu_screen/menu_screen.js";
import { TournamentService } from "../tournament/tournament_service.js";
import { debugTournamentAddCrowns, debugTournamentAdvanceBots, debugTournamentFillRoom, debugTournamentReset } from "../api/requests/tournament.requests.js";
import { BoosterHighlightRenderer } from "../ui/overlay/booster_highlight_renderer.js";
import { OverlayFactory } from "../ui/overlay/overlay_factory.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { OverlayId } from "../ui/overlay/overlay_ids.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { TournamentDialog } from "../ui/dialog/tournament_dialog.js";
import { TournamentWaitDialog } from "../ui/dialog/tournament_wait_dialog.js";
import { RateGameDialog } from "../ui/dialog/rate_game_dialog.js";
import { GoStoreDialog } from "../ui/dialog/go_store_dialog.js";
import { UpdateRequiredDialog, UpdateRequiredMode } from "../ui/dialog/update_required_dialog.js";
import { openPlacementOffer } from "../offers/placement_offer_registry.js";
import { MYSTERY_BOX_PLACEMENT, getMysteryBoxPossibleRewards, rollMysteryBoxPreviewRewards } from "../offers/mystery_box_offer.js";
import { MysteryBoxDialog } from "../ui/dialog/mystery_box_dialog.js";
import { KeyHuntDialog } from "../ui/dialog/key_hunt/key_hunt_dialog.js";
import { KeyHuntService } from "../key_hunt/key_hunt_service.js";
import { PrizeShotService } from "../prize_shot/prize_shot_service.js";
import { PrizeShotScreen } from "../prize_shot/prize_shot_screen.js";
import * as MaxAds from "../ads/applovin_max.js";
import { NoLevelsDialog } from "../ui/dialog/no_levels_dialog.js";
import { PlayDialog, PlayDialogDifficulty, PlayDialogState } from "../ui/dialog/play_dialog.js";
import { NetworkManager } from "../network_manager.js";
import { BuyBoosterDialog } from "../ui/dialog/buy_booster_dialog.js";
import { BuyBoosterType } from "../ui/dialog/buy_booster_types.js";
import { LocalizationManager } from "../localization/localization_manager.js";
import { resetDeviceIdForNewUser } from "../utils/device_id.js";
import { Vfx2DRuntime } from "../webgl_2d/vfx_2d_runtime.js";
import { AvatarSelectionDialog } from "../ui/dialog/avatar_selection_dialog.js";
import { SettingsDialog } from "../ui/dialog/settings_dialog.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { MoreLivesDialog } from "../ui/dialog/more_lives_dialog.js";
import { ProfileDialog } from "../ui/dialog/profile_dialog.js";
import { debugResetOfferState, debugSetLevel, resetProgress, rewardHeart } from "../api/requests/economy.requests.js";
import {
  ONE_PLUS_ONE_PLACEMENT,
  ONE_PLUS_ONE_TEMPLATE,
  ONE_PLUS_TWO_PLACEMENT,
  ONE_PLUS_TWO_TEMPLATE,
} from "../offers/one_plus_one_offer.js";
import type { OfferDto } from "../api/dto/offers.dto.js";
import { getMaxCampaignLevel } from "../level_config.js";
import { adoptEconomyFromPlayer } from "../api/player_sync.js";
import { PhysicsDebugRenderer } from "./physics_debug_renderer.js";
import { BallTuner } from "./ball_tuner.js";
import { BlockMassTuner } from "./block_mass_tuner.js";
import { Renderer } from "../core/renderer.js";
import { SceneManager } from "../core/scene_manager.js";
import { PixelFeatures, applyPixelFeatures } from "./pixel_features.js";
import { GRAPHICS_QUALITY_ORDER, GraphicsProfile, GraphicsQuality, GraphicsSettings } from "../graphics/graphics_quality.js";
import { FeatureTutorialService } from "../feature_tutorial/feature_tutorial_service.js";
import type { GameEventID } from "../events_config/events_config_service.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { CannonBall } from "../entities/cannon_ball.js";
import { Vfx3DRuntime } from "../vfx_3d/vfx_3d_runtime.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { VfxSplashBatch } from "../vfx_3d/core/batches/vfx_splash_batch.js";
import { VfxShardBatch } from "../vfx_3d/core/batches/vfx_shard_batch.js";
import { Vfx3DGlobalAtlas } from "../vfx_3d/core/textures/vfx_3d_global_atlas.js";
import { CumulativeBoosterID } from "../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { NotificationManager } from "../notifications/notification_manager.js";
import { OfferService } from "../offers/offer_service.js";
import { CustomEventsController } from "../socket_handler/controllers/custom_events_controller.js";
import { S2SEventContextService } from "../s2s/s2s_event_context.js";
import { S2SEventOutbox } from "../s2s/s2s_event_outbox.js";

let entry_attached = false;
let tap_code_attached = false;
let tap_code_index = 0;
let tap_code_start_ts = 0;

const diag_render_scales = Array.from( new Set( [ window.devicePixelRatio || 1, 2, 1 ] ) );
let diag_scale_index = 0;
let diag_solver_iters = 8;

function runTournamentCheat( label: string, request: () => Promise<unknown> ): void
{
  void request()
    .then( result =>
    {
      console.log( "[tournament cheat]", label, result );
      return TournamentService.fetch( true );
    } )
    .then( () =>
    {
      MenuScreen.active?.refreshTournamentProgress();
      console.log( "[tournament]", TournamentService.debugSummary() );
    } )
    .catch( e => console.warn( "[tournament cheat]", label, e ) );
}

function activeGameScreen(): GameScreen | null
{
  const screen = ScreenManager.getCurrentScreenInstance();
  return screen instanceof GameScreen ? screen : null;
}

function grantBoosterUse( id: CumulativeBoosterID ): void
{
  const screen = activeGameScreen();
  if ( !screen )
  {
    console.warn( "[DebugConsole] Not currently in a game — open a level first" );
    return;
  }

  screen.addBoosterUses( id, 1 );
  updateInfoText();
}

function goToLevelPrompt(): void
{
  const current = StatisticsStorage.load().level;
  const input = window.prompt( "Go to level:", String( current ) );
  if ( input === null )
    return;

  const level = Math.trunc( Number( input ) );
  if ( !Number.isFinite( level ) || level < 1 )
  {
    window.alert( "Enter a valid level number" );
    return;
  }

  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

async function openDebugBuyBoosterDialog(
  booster_type: BuyBoosterType,
  booster_id: CumulativeBoosterID
): Promise<void>
{
  DebugConsoleManager.close();
  const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer( booster_id );
  if ( offer === null )
  {
    console.warn( `[Boosters] No price offer for booster ${booster_id}` );
    return;
  }

  BuyBoosterDialog.open( {
    booster_type,
    booster_id,
    offer,
    source: "debug",
  } );
}

function diagToggleShadows(): void
{
  const renderer = Renderer.renderer;
  renderer.shadowMap.enabled = !renderer.shadowMap.enabled;
  renderer.shadowMap.needsUpdate = true;
  SceneManager.world.traverse( obj =>
  {
    const material = ( obj as { material?: unknown } ).material;
    if ( !material ) return;
    const materials = Array.isArray( material ) ? material : [ material ];
    for ( const m of materials )
      ( m as { needsUpdate?: boolean } ).needsUpdate = true;
  } );
}

function diagCycleRenderScale(): void
{
  diag_scale_index = ( diag_scale_index + 1 ) % diag_render_scales.length;
  Renderer.setDiagnosticPixelRatio( diag_render_scales[diag_scale_index] );
}

function diagToggleSolverIters(): void
{
  diag_solver_iters = diag_solver_iters === 8 ? 0 : 8;
  PhysicsWorld.getWorld().forEachRigidBody( body => body.setAdditionalSolverIterations( diag_solver_iters ) );
}

function diagRefreshMaterials(): void
{
  SceneManager.world.traverse( obj =>
  {
    const material = ( obj as { material?: unknown } ).material;
    if ( !material ) return;
    const materials = Array.isArray( material ) ? material : [ material ];
    for ( const m of materials )
      ( m as { needsUpdate?: boolean } ).needsUpdate = true;
  } );
}

const SHADOW_PRESETS = [
  { label: "PCF 2048", type: THREE.PCFShadowMap, size: 2048 },
  { label: "PCF 1024", type: THREE.PCFShadowMap, size: 1024 },
  { label: "BASIC 1024", type: THREE.BasicShadowMap, size: 1024 },
];
let diag_shadow_index = 0;

function diagCycleShadowQuality(): void
{
  diag_shadow_index = ( diag_shadow_index + 1 ) % SHADOW_PRESETS.length;
  const preset = SHADOW_PRESETS[diag_shadow_index];

  Renderer.renderer.shadowMap.type = preset.type;

  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.DirectionalLight;
    if ( !( light as { isDirectionalLight?: boolean } ).isDirectionalLight || !light.castShadow ) return;
    light.shadow.mapSize.set( preset.size, preset.size );
    light.shadow.map?.dispose();
    light.shadow.map = null as any;
  } );

  diagRefreshMaterials();
  Renderer.renderer.shadowMap.needsUpdate = true;
}

let diag_receive_shadows = true;

function diagToggleReceiveShadows(): void
{
  diag_receive_shadows = !diag_receive_shadows;
  SceneManager.world.traverse( obj =>
  {
    const mesh = obj as THREE.Mesh;
    if ( !( mesh as { isMesh?: boolean } ).isMesh ) return;
    const material = mesh.material;
    const first = Array.isArray( material ) ? material[0] : material;
    // The ground plane is the ShadowMaterial catcher — it must keep receiving,
    // otherwise the whole table shadow disappears and the test means nothing.
    if ( first && ( first as { isShadowMaterial?: boolean } ).isShadowMaterial ) return;
    mesh.receiveShadow = diag_receive_shadows;
  } );
  diagRefreshMaterials();
  Renderer.renderer.shadowMap.needsUpdate = true;
}

let diag_ccd = true;

function diagToggleCcd(): void
{
  diag_ccd = !diag_ccd;
  PhysicsWorld.getWorld().forEachRigidBody( body => body.enableCcd( diag_ccd ) );
}

// Each directional light costs a full BRDF evaluation per pixel on every PBR
// surface — and twice that where clearcoat is enabled. Toggling them one by one
// separates "cost of light" from "cost of material".
function diagToggleClearcoat(): void
{
  PixelFeatures.clearcoat = !PixelFeatures.clearcoat;
  applyPixelFeatures();
}

function diagToggleNormalMaps(): void
{
  PixelFeatures.normal_maps = !PixelFeatures.normal_maps;
  applyPixelFeatures();
  console.log( `[BLOCK-MODEL-DEBUG] normal maps ${PixelFeatures.normal_maps ? "ON" : "OFF"}` );
}

function diagToggleEmissiveMaps(): void
{
  PixelFeatures.emissive_maps = !PixelFeatures.emissive_maps;
  applyPixelFeatures();
}

const LIGHT_MODES = [ "ALL", "SUN+AMB", "AMB ONLY" ];
let diag_light_mode = 0;

function diagToggleFrontLight(): void
{
  GlobalSettings.DEBUG_NO_FRONT_LIGHT = !GlobalSettings.DEBUG_NO_FRONT_LIGHT;

  const no_sun = GlobalSettings.DEBUG_NO_FRONT_LIGHT;

  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.Light & { isDirectionalLight?: boolean; isAmbientLight?: boolean; isHemisphereLight?: boolean };

    if ( light.isDirectionalLight )
      light.visible = !no_sun;
    else if ( light.isAmbientLight )
      light.intensity = no_sun ? 1.05 : 0.55;
    else if ( light.isHemisphereLight )
      light.intensity = no_sun ? 1.7 : 0.95;
  } );

  Renderer.renderer.shadowMap.needsUpdate = true;
}

function diagCycleLights(): void
{
  diag_light_mode = ( diag_light_mode + 1 ) % LIGHT_MODES.length;

  const directionals: THREE.DirectionalLight[] = [];
  SceneManager.world.traverse( obj =>
  {
    const light = obj as THREE.DirectionalLight;
    if ( ( light as { isDirectionalLight?: boolean } ).isDirectionalLight )
      directionals.push( light );
  } );

  // The shadow caster is the brightest one; keep it as the "sun".
  directionals.sort( ( a, b ) => Number( b.castShadow ) - Number( a.castShadow ) || b.intensity - a.intensity );

  directionals.forEach( ( light, index ) =>
  {
    if ( diag_light_mode === 0 ) light.visible = true;
    else if ( diag_light_mode === 1 ) light.visible = index === 0;
    else light.visible = false;
  } );

  diagRefreshMaterials();
}

// PBR keeps its cost per pixel regardless of light count: normal maps,
// clearcoat and envMap each add texture fetches and BRDF work. These variants
// strip them one tier at a time so the quality/FPS trade-off is visible.
const MATERIAL_MODES = [ "PBR", "SMART", "LITE", "PHONG", "BASIC" ] as const;
let diag_material_mode = 0;

const diag_saved_materials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
const diag_variant_cache = new Map<string, THREE.Material>();

function materialVariant( source: THREE.Material, mode: number ): THREE.Material
{
  const key = `${source.uuid}|${mode}`;
  const cached = diag_variant_cache.get( key );
  if ( cached ) return cached;

  const src = source as THREE.MeshStandardMaterial;
  const color = src.color ? src.color.clone() : new THREE.Color( 0xffffff );
  const common = {
    map: src.map ?? null,
    transparent: src.transparent,
    opacity: src.opacity,
    side: src.side,
    alphaTest: src.alphaTest,
  };

  let variant: THREE.Material;

  // SMART: big static surfaces (table) drop to Phong since their lighting
  // barely changes, while gameplay blocks keep PBR shading minus the extras.
  const effective_mode = mode === 1
    ? ( source.userData?.perf_tier === "large_static" ? 3 : 2 )
    : mode;

  if ( effective_mode === 2 )
  {
    // Same lighting model, minus the per-pixel extras.
    variant = new THREE.MeshStandardMaterial( {
      ...common,
      color,
      roughness: src.roughness ?? 0.5,
      metalness: src.metalness ?? 0,
    } );
  }
  else if ( effective_mode === 3 )
  {
    variant = new THREE.MeshPhongMaterial( {
      ...common,
      color,
      shininess: THREE.MathUtils.clamp( ( 1 - ( src.roughness ?? 0.5 ) ) * 90, 4, 90 ),
      specular: new THREE.Color( 0x2a2a2a ),
    } );
  }
  else
  {
    variant = new THREE.MeshBasicMaterial( { ...common, color } );
  }

  diag_variant_cache.set( key, variant );
  return variant;
}

function diagCycleMaterials(): void
{
  diag_material_mode = ( diag_material_mode + 1 ) % MATERIAL_MODES.length;

  SceneManager.world.traverse( obj =>
  {
    const mesh = obj as THREE.Mesh;
    if ( !( mesh as { isMesh?: boolean } ).isMesh || !mesh.material ) return;

    if ( !diag_saved_materials.has( mesh ) )
    {
      const current = Array.isArray( mesh.material ) ? mesh.material[0] : mesh.material;
      if ( ( current as { isMeshStandardMaterial?: boolean } ).isMeshStandardMaterial !== true ) return;
      diag_saved_materials.set( mesh, mesh.material );
    }

    const original = diag_saved_materials.get( mesh )!;

    if ( diag_material_mode === 0 )
    {
      mesh.material = original;
      return;
    }

    mesh.material = Array.isArray( original )
      ? original.map( m => materialVariant( m, diag_material_mode ) )
      : materialVariant( original, diag_material_mode );
  } );
}

let panel_el: HTMLDivElement | null = null;
let info_el: HTMLPreElement | null = null;
let dynamic_buttons: { btn: HTMLButtonElement; cfg: ButtonConfig }[] = [];
let active_debug_page = 0;
let add_heart_inflight = false;
let set_level_inflight = false;
let reset_offer_inflight = false;
let reset_progress_inflight = false;
let reset_adventure_inflight = false;
let notifications_debug_text = "";
let s2s_debug_text = "";

function maskIdentifier( value: string | undefined ): string
{
  if ( !value ) return "-";
  return value.length <= 8 ? value : `${value.slice( 0, 4 )}…${value.slice( -4 )}`;
}

function refreshS2SDebugText(): void
{
  const consent = S2SEventContextService.getConsentSnapshot();
  const ids = S2SEventContextService.getStoredIdentifiers();
  const status = CustomEventsController.getDebugStatus();
  const queue = S2SEventOutbox.list();
  const lines = [
    `Session: ${status.session_ready ? "ready" : "not ready"}${status.in_flight_event_id ? `, in flight ${status.in_flight_event_id.slice( 0, 8 )}` : ""}`,
    `Consent: ${consent.region} · analytics ${consent.analytics_allowed ? "Y" : "N"} · ad measurement ${consent.ad_measurement_allowed ? "Y" : "N"} · personalization ${consent.ad_personalization_allowed ? "Y" : "N"} · ${consent.policy_version}`,
    `AppsFlyer id: ${maskIdentifier( ids.appsflyer_id )}`,
    `GAID: ${maskIdentifier( ids.gaid )}`,
    `Play referrer gclid: ${maskIdentifier( ids.market_referrer_gclid )} · gbraid: ${maskIdentifier( ids.gbraid )}`,
    `First open: ${ids.first_open_time}`,
    `Queue: ${queue.length}`,
    ...queue.slice( 0, 8 ).map( entry => `  ${String( entry.event.payload.name )} ${entry.event.event_id.slice( 0, 8 )} · attempts ${entry.attempts}${entry.next_attempt_at > Date.now() ? ` · next in ${Math.ceil( ( entry.next_attempt_at - Date.now() ) / 1000 )}s` : ""}` ),
  ];
  s2s_debug_text = lines.join( "\n" );
  updateInfoText();
}

const getTapQuadrant = ( x: number, y: number ): number =>
{
  const w = window.innerWidth || document.documentElement.clientWidth || 1;
  const h = window.innerHeight || document.documentElement.clientHeight || 1;
  const is_top = y < h / 2;
  const is_left = x < w / 2;
  if ( is_top && is_left ) return 1;
  if ( is_top && !is_left ) return 2;
  if ( !is_top && is_left ) return 3;
  return 4;
};

const resetTapCode = (): void =>
{
  tap_code_index = 0;
  tap_code_start_ts = 0;
};

const registerTapCodeStep = ( quadrant: number ): void =>
{
  const code = DebugSettings.DEBUG_CONSOLE_TAP_CODE;
  const window_ms = DebugSettings.DEBUG_CONSOLE_TAP_CODE_WINDOW_MS;
  if ( !code || code.length === 0 ) return;

  const now = performance.now();
  if ( tap_code_index === 0 )
  {
    tap_code_start_ts = now;
  }
  else if ( now - tap_code_start_ts > window_ms )
  {
    tap_code_index = 0;
    tap_code_start_ts = now;
  }

  if ( quadrant !== code[ tap_code_index ] )
  {
    tap_code_index = 0;
    tap_code_start_ts = now;
    if ( quadrant !== code[ 0 ] ) return;
  }

  if ( quadrant === code[ tap_code_index ] )
  {
    tap_code_index += 1;
    if ( tap_code_index >= code.length )
    {
      resetTapCode();
      if ( GlobalSettings.IS_PROD )
        DebugConsoleManager.promptPassword();
      else
        DebugConsoleManager.open();
    }
  }
};

if ( !GlobalSettings.IS_PROD )
{
  ( window as any ).brickBench = BrickBench;
  /** Any overlay by id, e.g. openOverlay('ADVENTURE_INFO_OVERLAY'). */
  ( window as any ).openOverlay = ( id: OverlayId, params?: unknown ) => OverlayFactory.openOverlay( id, params as never );
  ( window as any ).keyHuntDebug = {
    open: () => { DialogManager.closeAllDialogs(); KeyHuntDialog.open(); },
    state: () => KeyHuntService.get(),
    payload: () => KeyHuntService.getPayload(),
    refresh: () => KeyHuntService.fetch( true ),
    reset: () => KeyHuntService.debugReset(),
    complete: ( task_id?: string ) => KeyHuntService.debugComplete( task_id ),
    expire: () => KeyHuntService.debugExpire(),
    /** Hit path of a tap at canvas px (x, y) in the top dialog. */
    hit: ( x: number, y: number ) => DialogManager.getTopDialog()?.debugTouchHitPath( new Vector2( x - Game.safeAreaLeft(), y - Game.safeAreaTop() ) ),
    /** The open dialog itself, e.g. `keyHuntDebug.top().growKeyBar(2)` to replay the key bar fill. */
    top: () => DialogManager.getTopDialog(),
  };
  ( window as any ).prizeShotDebug = {
    open: () => { DebugConsoleManager.close(); void ScreenManager.showScreen( Screens.GAME, { prize_shot: true } ); },
    round: () => PrizeShotService.round(),
    config: () => PrizeShotService.getConfig(),
    /** As on the first visit: a fresh board, the free balls, no coin continues bought; reopens an open round. */
    reset: () => resetPrizeShotToStart(),
    shots: ( n = 3 ) => PrizeShotService.debugSetShots( n ),
    /** Coin continues bought this round; 0 makes "Buy for coins" available again. */
    continues: ( n = 0 ) => PrizeShotService.debugSetContinues( n ),
    screen: () => ScreenManager.getCurrentScreenInstance(),
    /** The open overlay (rewards, Continue), e.g. `prizeShotDebug.overlay().claim()` in the perf stand. */
    overlay: () => OverlayManager.getTopOverlay(),
    /** The level loading screen still covers the round. */
    loading: () => Game.instance.isLoadingGameScreenShown(),
    /** What the idle lobby does ahead of the first entry. */
    prefetch: () => PrizeShotScreen.prefetchAssets(),
  };
  ( window as any ).royalWheelDebug = {
    open: () => RoyalWheelDebug.open(),
    close: () => RoyalWheelDebug.close(),
    spin: ( times = 1 ) => RoyalWheelDebug.spin( times ),
    collect: () => RoyalWheelDebug.collect(),
    tokens: ( amount = 10 ) => RoyalWheelDebug.addTokens( amount ),
    state: () => RoyalWheelDebug.getState(),
    hits: () => RoyalWheelDebug.getHits(),
  };
  ( window as any ).__perf = {
    prewarmLog: () => DialogManager.getPrewarmLog(),
    pooledDialogs: () => DialogManager.pooledNames(),
    releaseDialogPool: () => DialogManager.releasePool( "debug" ),
    bodies: () =>
    {
      let n = 0;
      PhysicsWorld.getWorld().forEachRigidBody( () => { n++; } );
      return n;
    },
    liveDebris: () => VfxBlockShatterTask.hasLiveDebris(),
    shadowDebug: () =>
    {
      const d = { ...GameScreen.shadow_debug };
      GameScreen.shadow_debug = { frames: 0, refresh: 0, cannon: 0, count: 0, debris: 0, entities: 0 };
      return d;
    },
    graphics: () => GraphicsSettings.describe(),
    setGraphics: ( quality: GraphicsQuality ) => GraphicsSettings.set( quality ),
    setGraphicsOverride: ( override: Partial<GraphicsProfile> | null ) => GraphicsSettings.setOverride( override ),
    state: () => ( {
      graphics: GraphicsSettings.get(),
      msaa: Renderer.hasMsaa(),
      shadows: Renderer.renderer?.shadowMap.enabled ?? null,
      shadow_preset: SHADOW_PRESETS[diag_shadow_index].label,
      recv_shadows: diag_receive_shadows,
      ccd: diag_ccd,
      solver_iters: diag_solver_iters,
      clearcoat: PixelFeatures.clearcoat,
      normal_maps: PixelFeatures.normal_maps,
      emissive_maps: PixelFeatures.emissive_maps,
      materials: MATERIAL_MODES[diag_material_mode],
      lights: LIGHT_MODES[diag_light_mode],
      front_light: !GlobalSettings.DEBUG_NO_FRONT_LIGHT,
      vfx3d: !GlobalSettings.DEBUG_DISABLE_VFX3D,
      ball_shadow: CannonBall.cast_shadow,
      autofire: StressTest.isAutofiring(),
      stress_index: StressTest.getIndex(),
    } ),
    setShadows: ( on: boolean ) => { if ( ( Renderer.renderer?.shadowMap.enabled ?? false ) !== on ) diagToggleShadows(); },
    setRecvShadows: ( on: boolean ) => { if ( diag_receive_shadows !== on ) diagToggleReceiveShadows(); },
    setCcd: ( on: boolean ) => { if ( diag_ccd !== on ) diagToggleCcd(); },
    setClearcoat: ( on: boolean ) => { if ( PixelFeatures.clearcoat !== on ) diagToggleClearcoat(); },
    setNormalMaps: ( on: boolean ) => { if ( PixelFeatures.normal_maps !== on ) diagToggleNormalMaps(); },
    setEmissiveMaps: ( on: boolean ) => { if ( PixelFeatures.emissive_maps !== on ) diagToggleEmissiveMaps(); },
    setBallShadow: ( on: boolean ) => { CannonBall.cast_shadow = on; },
    setVfx3d: ( on: boolean ) =>
    {
      GlobalSettings.DEBUG_DISABLE_VFX3D = !on;
      if ( !on ) Vfx3DRuntime.killAllBatches();
    },
    setMaterials: ( mode: string ) =>
    {
      let guard = 0;
      while ( MATERIAL_MODES[diag_material_mode] !== mode && guard++ < MATERIAL_MODES.length )
        diagCycleMaterials();
    },
    setLights: ( mode: string ) =>
    {
      let guard = 0;
      while ( LIGHT_MODES[diag_light_mode] !== mode && guard++ < LIGHT_MODES.length )
        diagCycleLights();
    },
    setShadowPreset: ( label: string ) =>
    {
      let guard = 0;
      while ( SHADOW_PRESETS[diag_shadow_index].label !== label && guard++ < SHADOW_PRESETS.length )
        diagCycleShadowQuality();
    },
    setSolverIters: () => diagToggleSolverIters(),
    setAutofire: ( on: boolean ) => StressTest.setAutofire( on ),
    benchOpen: () => TriangleBench.open(),
    benchClose: () => TriangleBench.close(),
    benchActive: () => TriangleBench.isActive(),
    benchStats: () => TriangleBench.getStats(),
    benchLost: () => TriangleBench.isContextLost(),
    benchViewport: () => TriangleBench.viewportInfo(),
    benchEvents: () => TriangleBench.contextEvents(),
    benchTexture: ( size: number ) => TriangleBench.setTexture( size ),
    benchSlot: ( slot: string, on: boolean ) => TriangleBench.setSlot( slot as never, on ),
    benchReset: () => TriangleBench.resetStats(),
    benchCount: ( n: number ) => TriangleBench.setCount( n ),
    benchSize: ( s: number ) => TriangleBench.setSize( s ),
    benchMaterial: ( m: string ) => TriangleBench.setMaterial( m as never ),
    benchSpin: ( on: boolean ) => TriangleBench.setSpin( on ),
    benchShadows: ( on: boolean ) => TriangleBench.setShadows( on ),
    benchLights: ( kind: string, n: number ) => TriangleBench.setLights( kind as never, n ),
    plainBall: ( on: boolean ) => { CannonBall.force_plain = on; },
    mapsCreated: () => VfxBlockShatterTask.mapsCreated(),
    debrisBodies: ( n: number | null ) => { VfxBlockShatterTask.promoted_override = n; },
    maxSteps: ( n: number ) => { Game.max_physics_steps = n; },
    physBudget: ( ms: number ) => { Game.physics_budget_ms = ms; },
    physAdaptive: ( on: boolean ) =>
    {
      Game.physics_adaptive = on;
      if ( !on ) Game.physics_slow_mode = false;
    },
    physSlow: ( on: boolean ) => { Game.physics_slow_mode = on; },
    physState: () => ( {
      max_steps: Game.max_physics_steps,
      budget_ms: Game.physics_budget_ms,
      adaptive: Game.physics_adaptive,
      slow_mode: Game.physics_slow_mode,
    } ),
    solverIters: ( n: number ) =>
    {
      PhysicsWorld.getWorld().numSolverIterations = n;
      PhysicsWorld.getWorld().forEachRigidBody( body => body.setAdditionalSolverIterations( 0 ) );
    },
    physCounts: () => PhysicsWorld.counts(),
    vfxStats: () => Vfx2DRuntime.getDetailedStats(),
    vfx3dStats: () => Vfx3DRuntime.getDetailedStats(),
    openResultOverlay: ( stars: number = 3, then?: "menu" | "level" ) => openDebugWinOverlay( stars, then ),
    openShopOverlay: () => OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, { onShopClosed: () => {} } ),
    openContinueOverlay: ( shots: number = 5, price: number = 900 ) => OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, { offer: { price, shots }, purchase_index: 0, level: StatisticsStorage.load().level, onClose: () => {}, onBuy: () => {} } ),
    openAvatarDialog: () => { DialogManager.openDialog( new AvatarSelectionDialog( () => {} ) ); },
    openProfileDialog: () => { ProfileDialog.open(); },
    openSettingsDialog: () => { SettingsDialog.open(); },
    openMoreLivesDialog: () => { MoreLivesDialog.open(); },
    closeDialogs: () => { DialogManager.closeAllDialogs(); },
    setLevel: ( level: number ) => setLevelOnServer( level ),
    closeOverlays: () => { while ( OverlayManager.isAnyOverlayOpened() ) OverlayManager.closeOverlay(); },
    openMysteryBoxPreview: () => { DialogManager.openDialog( new MysteryBoxDialog( null, { placement_id: MYSTERY_BOX_PLACEMENT } ) ); },
    openMysteryBoxOverlay: () => { OverlayFactory.openOverlay( OverlayId.MYSTERY_BOX_OVERLAY, { rewards: rollMysteryBoxPreviewRewards( getMysteryBoxPossibleRewards( null ) ), stats_before: StatisticsStorage.load() } ); },
    topOverlay: () => OverlayManager.getTopOverlay(),
    claimMysteryBox: () =>
    {
      const overlay = OverlayManager.getTopOverlay() as any;
      if ( !overlay || overlay.name() !== "MysteryBoxOverlay" ) return "no";
      if ( overlay.claimed ) return "claimed";
      overlay.claim();
      return "ok";
    },
    simulateEventExpiry: () =>
    {
      const now = Date.now();
      const iso = new Date( now ).toISOString();
      const ui = ( ScreenManager as any ).current_ui;
      const name = ui?.name?.();
      if ( name === "MenuScreen" )
      {
        ui.daily_week_end_ms = now;
        ui.adventure_period_end_ms = now;
        ui.daily_rollover_attempt_at = 0;
        ui.adventure_rollover_attempt_at = 0;
        ui.daily_challenge_rollover_attempt_at = 0;
      }
      if ( name === "DailyTasksScreen" )
      {
        ui.week_end_ms = now;
        ui.rollover_attempt_at = 0;
      }
      if ( name === "DailyChallengeScreen" )
        ui.rollover_attempt_at = 0;
      ( DailyChallengeService as any ).period_end_at = now;
      const tower = ( WinStreakTowerService as any ).payload?.tower;
      if ( tower ) tower.period_end = iso;
      const daily = ( DailyTasksService as any ).payload;
      if ( daily )
      {
        daily.period_end = iso;
        if ( daily.stars ) daily.stars.week_end = iso;
      }
      return { screen: name ?? null };
    },
    eventState: () => ( {
      screen: ( ScreenManager as any ).current_ui?.name?.() ?? null,
      menu_daily_end: ( ScreenManager as any ).current_ui?.daily_week_end_ms ?? null,
      menu_adventure_end: ( ScreenManager as any ).current_ui?.adventure_period_end_ms ?? null,
      tasks_screen_end: ( ScreenManager as any ).current_ui?.week_end_ms ?? null,
      dc_end: ( DailyChallengeService as any ).period_end_at,
      tower_end: ( WinStreakTowerService as any ).payload?.tower?.period_end ?? null,
      tasks_end: ( DailyTasksService as any ).payload?.stars?.week_end ?? null,
      now: Date.now(),
    } ),
    fpsOverlay: ( enabled: boolean = true ) => Game.instance.setFpsOverlayEnabled( enabled ),
    profStart: () => FpsOverlay.profileStart(),
    profStop: () => FpsOverlay.profileStop(),
    profStats: () => FpsOverlay.profileStats(),
    rimMode: ( mode: string ) => RimLightTest.setMode( mode as never ),
    rimColor: ( hex: number ) => RimLightTest.setColor( hex ),
    rimIntensity: ( value: number ) => RimLightTest.setIntensity( value ),
    rimState: () => RimLightTest.getState(),
    rimReset: () => RimLightTest.reset(),
    inspectorOpen: () => openMaterialInspector(),
    ballTuner: () => BallTuner.toggle(),
    ballSpeed: ( value: number ) => BallTuner.setSpeed( value ),
    ballMass: ( value: number ) => BallTuner.setMass( value ),
    ballReset: () => BallTuner.reset(),
    blockMass: ( value: number ) => TextureTest.setBlockMass( value ),
    blockMassState: () => ( {
      block: BlockMassTuner.getActiveKey(),
      base: BlockMassTuner.getBaseMass(),
      mass: BlockMassTuner.getMass(),
      modified: BlockMassTuner.modifiedCount(),
    } ),
    blockMassReset: () => TextureTest.resetMasses(),
    inspectorReset: () => ( window as any ).__inspector?.reset(),
    textureCount: () => TextureTest.COUNT,
    textureNames: () => TextureTest.names(),
    textureIndex: () => TextureTest.getIndex(),
    textureName: () => TextureTest.getName(),
    textureRun: ( index: number ) =>
    {
      const level = TextureTest.activate( index );
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    textureNext: () =>
    {
      void ScreenManager.showScreen( Screens.GAME, { level: TextureTest.advance() } );
    },
    stressRun: ( index: number ) =>
    {
      const level = StressTest.activate( index );
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    replay: () =>
    {
      void ScreenManager.showScreen( Screens.GAME, { level: StressTest.currentLevel() } );
    },
    goLevel: ( level: number ) =>
    {
      DebugConsoleManager.close();
      Game.instance.hideLoadingGameScreen();
      void ScreenManager.showScreen( Screens.GAME, { level } );
    },
    bhState: () => BoosterHighlightRenderer.diagnostics(),
    bhReset: () => BoosterHighlightRenderer.resetDiagnostics(),
  };
}

let inspector_loading = false;

async function openMaterialInspector(): Promise<void>
{
  const api = ( window as any ).__inspector;
  if ( api )
  {
    api.show();
    return;
  }

  if ( inspector_loading ) return;
  inspector_loading = true;

  try
  {
    const response = await fetch( `inspector.js?v=${Date.now()}` );
    if ( !response.ok ) throw new Error( `HTTP ${response.status}` );
    const source = await response.text();
    ( 0, eval )( source );
    ( window as any ).__inspector?.show();
  }
  catch ( error )
  {
    console.error( "[MaterialInspector] не загрузился:", error );
  }
  finally
  {
    inspector_loading = false;
  }
}

function runTextureLevel( index: number ): void
{
  const level = TextureTest.activate( index );
  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

TextureTest.show_level = ( level: number ) =>
{
  void ScreenManager.showScreen( Screens.GAME, { level } );
};

function runStressLevel( index: number ): void
{
  const level = StressTest.activate( index );
  DebugConsoleManager.close();
  StressRenderHud.start();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

function runBarrierLine( index: number ): void
{
  const level = BarrierShowcase.activateLine( index );
  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

function runTableFormationLevel( index: number ): void
{
  const level = TableFormationShowcase.activate( index );
  DebugConsoleManager.close();
  Game.instance.hideLoadingGameScreen();
  void ScreenManager.showScreen( Screens.GAME, { level } );
}

type ButtonConfig = { text?: string; getText?: () => string; action: () => void };
type SectionConfig = { title: string; buttons: ButtonConfig[] };
type DebugPage = { title: string; sections: SectionConfig[] };

function buildPanel(): HTMLDivElement
{
  const overlay = document.createElement( "div" );
  overlay.id = "debugConsolePanel";
  overlay.style.position = "fixed";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.right = "0";
  overlay.style.bottom = "0";
  overlay.style.zIndex = "2147483647";
  overlay.style.background = "rgba(10, 12, 20, 0.94)";
  overlay.style.color = "#fff";
  overlay.style.font = "13px Arial, sans-serif";
  overlay.style.overflowY = "auto";
  overlay.style.boxSizing = "border-box";
  // Keep the CLOSE header out from under the notch / Telegram top bar.
  // --tg-safe-* come from TelegramAPISettings.applySafeAreaCssVariables (Telegram insets merged with env()).
  overlay.style.paddingTop = "calc(16px + var(--tg-safe-top, env(safe-area-inset-top, 0px)))";
  overlay.style.paddingBottom = "calc(16px + var(--tg-safe-bottom, env(safe-area-inset-bottom, 0px)))";
  overlay.style.paddingLeft = "calc(16px + var(--tg-safe-left, env(safe-area-inset-left, 0px)))";
  overlay.style.paddingRight = "calc(16px + var(--tg-safe-right, env(safe-area-inset-right, 0px)))";
  overlay.style.display = "none";

  const header = document.createElement( "div" );
  header.style.display = "flex";
  header.style.justifyContent = "space-between";
  header.style.alignItems = "center";
  header.style.marginBottom = "16px";

  const title = document.createElement( "div" );
  title.textContent = "DEBUG CONSOLE";
  title.style.font = "700 18px Arial, sans-serif";

  const close_btn = document.createElement( "button" );
  close_btn.type = "button";
  close_btn.textContent = "CLOSE";
  styleButton( close_btn, "#c62828" );
  close_btn.style.minHeight = "56px";
  close_btn.style.padding = "16px 18px";
  close_btn.style.fontSize = "14px";
  close_btn.addEventListener( "click", () => DebugConsoleManager.close() );

  header.appendChild( title );
  header.appendChild( close_btn );
  overlay.appendChild( header );

  const sections: SectionConfig[] = [
    {
      title: "SCREENS",
      buttons: [
        { text: "MENU", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.MENU ); } },
        { text: "SHOP", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.SHOP ); } },
        { text: "LEADERBOARD", action: () => { Game.instance.hideLoadingGameScreen(); void ScreenManager.showScreen( Screens.LEADERBOARD ); } },
        { text: "SETTINGS", action: () => Game.instance.openSettingsDialog() },
        { text: "LOADING GAME", action: () => { DebugConsoleManager.close(); Game.instance.showLoadingGameScreen(); } },
        { text: "LOADING PRIZE SHOT (3s)", action: () => {
          DebugConsoleManager.close();
          Game.instance.showLoadingGameScreen( false, 'prize_shot' );
          window.setTimeout( () => { void ScreenManager.showScreen( Screens.MENU ).then( () => Game.instance.hideLoadingGameScreen() ); }, 3000 );
        } },
        { text: "REPLAY CART DRIVE", action: () => { DebugConsoleManager.close(); void ScreenManager.showScreen( Screens.MENU ).then( () => MenuScreen.active?.replayCartDrive() ); } },
      ],
    },
    {
      title: "LEVEL",
      buttons: [
        { text: "PLAY LEVEL... (no progress)", action: () => goToLevelPrompt() },
        { text: "ОБЗОР ПУЛА УРОВНЕЙ", action: () => { DebugConsoleManager.close(); LevelPoolBrowser.open(); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "LEVEL +1", action: () => { void shiftLevelOnServer( 1 ); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "LEVEL -1", action: () => { void shiftLevelOnServer( -1 ); } },
        { getText: () => set_level_inflight ? "SETTING LEVEL..." : "SET LEVEL...", action: () => { void setLevelPrompt(); } },
      ],
    },
    {
      title: "AUDIO",
      buttons: [
        { text: "TOGGLE SOUNDS", action: () => { const s = StatisticsStorage.load(); StatisticsStorage.update( { sounds_enabled: !s.sounds_enabled } ); updateInfoText(); } },
        { text: "TOGGLE MUSIC", action: () => { const s = StatisticsStorage.load(); StatisticsStorage.update( { music_enabled: !s.music_enabled } ); updateInfoText(); } },
      ],
    },
    {
      title: "LOCALIZATION",
      buttons: [
        { getText: () => `LANGUAGE: ${LocalizationManager.getCurrentLanguage().toUpperCase()}`, action: () => cycleLanguage() },
      ],
    },
    {
      title: "FEATURE TUTORIALS",
      buttons: [
        { text: "RESET FEATURE TUTORS", action: () => { FeatureTutorialService.reset(); updateInfoText(); } },
        ...( [
          [ "daily_tasks", "DAILY TASKS" ],
          [ "daily_challenge", "DAILY CHALLENGE" ],
          [ "chain_offer", "CHAIN OFFER" ],
          [ "win_streak_tower", "ADVENTURE" ],
        ] as [ GameEventID, string ][] ).map( ( [ event_id, label ] ) => ( {
          text: `TUTOR: ${label}`,
          action: () =>
          {
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.MENU ).then( () =>
            {
              if ( !MenuScreen.active?.debugStartFeatureTutorial( event_id ) )
                console.warn( `[Debug] feature tutorial widget for "${event_id}" is not on the menu (locked or hidden)` );
            } );
          },
        } ) ),
      ],
    },
    {
      title: "RESULT OVERLAY",
      buttons: [
        { text: "WIN 3★", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 3, rewards: [ { currency: "coin", amount: 120 } ] } ); } },
        { text: "WIN 2★ + HEART", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 2, rewards: [ { currency: "coin", amount: 80 }, { currency: "heart", amount: 1 } ] } ); } },
        { text: "WIN 3★ → MENU (COINS)", action: () => { DebugConsoleManager.close(); openDebugWinOverlay( 3, "menu" ); } },
        { text: "WIN 3★ → NEXT LEVEL (COINS)", action: () => { DebugConsoleManager.close(); openDebugWinOverlay( 3, "level" ); } },
        { text: "WIN 1★", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_PASSED", stars: 1, rewards: [ { currency: "coin", amount: 40 } ] } ); } },
        { text: "LOSE", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, { title_key: "RESULT.LEVEL_FAILED", stars: 0, rewards: [] } ); } },
        { text: "CONTINUE OVERLAY (test)", action: () => {
          DebugConsoleManager.close();
          OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, {
            offer: { price: 900, shots: 5 }, purchase_index: 0, level: StatisticsStorage.load().level,
            onClose: () => {}, onBuy: () => {},
          } );
        } },
      ],
    },
    {
      title: "DIALOGS",
      buttons: [
        { text: "ADVENTURE ENDED OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.ADVENTURE_ENDED_OVERLAY, { rewards: [ { type: "coin", amount: 1000 }, { type: "heart", amount: 5 }, { type: "fireworks", amount: 3 }, { type: "infinite_balls", amount: 23 }, { type: "multiballs", amount: 999 }, { type: "big_balls", amount: 2 }], } ); } },
        { text: "DAILY TASKS ENDED OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.DAILY_TASKS_ENDED_OVERLAY, { rewards: [ { type: "coin", amount: 1000 }, { type: "heart", amount: 5 }, { type: "fireworks", amount: 3 }, ], } ); } },
        { text: "DAILY TASKS OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.DAILY_TASKS_OVERLAY ); } },
        { text: "NEW BOOSTER FIREWORK", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.Firework } ); } },
        { text: "NEW BOOSTER FIVE BALLS", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.FiveBalls } ); } },
        { text: "NEW BOOSTER BIG BALL", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.BigBall } ); } },
        { text: "NEW BOOSTER INFINITE SHOTS", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, { booster_type: BuyBoosterType.InfiniteShots } ); } },
        { text: "BOOSTER FIREWORK", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.Firework, CumulativeBoosterID.Fireworks ); } },
        { text: "BOOSTER FIVE BALLS", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.FiveBalls, CumulativeBoosterID.Multiball ); } },
        { text: "BOOSTER BIG BALL", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.BigBall, CumulativeBoosterID.BigBall ); } },
        { text: "BOOSTER INFINITE SHOTS", action: () => { void openDebugBuyBoosterDialog( BuyBoosterType.InfiniteShots, CumulativeBoosterID.InfiniteBalls ); } },
        { text: "PLAY HARD", action: () => { DebugConsoleManager.close(); PlayDialog.open( StatisticsStorage.load().level, PlayDialogState.Play, PlayDialogDifficulty.Hard ); } },
        { text: "PLAY SUPER HARD", action: () => { DebugConsoleManager.close(); PlayDialog.open( StatisticsStorage.load().level, PlayDialogState.Play, PlayDialogDifficulty.SuperHard ); } },
        { text: "REMOVE ADS", action: () => { DebugConsoleManager.close(); openPlacementOffer( 'lobby_no_ads' ); } },
        // Real placement when the server has one; otherwise a local preview (no ad, local roll).
        { text: "MYSTERY BOX DIALOG", action: () => { DebugConsoleManager.close(); if ( !openPlacementOffer( MYSTERY_BOX_PLACEMENT ) ) DialogManager.openDialog( new MysteryBoxDialog( null, { placement_id: MYSTERY_BOX_PLACEMENT } ) ); } },
        // Just the chest opening with a local roll; grants nothing.
        { text: "MYSTERY BOX OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.MYSTERY_BOX_OVERLAY, { rewards: rollMysteryBoxPreviewRewards( getMysteryBoxPossibleRewards( null ) ), stats_before: StatisticsStorage.load() } ); } },
        { text: "KEY HUNT DIALOG", action: () => { DebugConsoleManager.close(); KeyHuntDialog.open(); } },
        { text: "ROYAL WHEEL (DEBUG WHEEL)", action: () => { DebugConsoleManager.close(); RoyalWheelDebug.open(); } },
        { text: "KEY HUNT QA: RESET", action: () => { void KeyHuntService.debugReset().catch( e => console.warn( "[KeyHunt] debug reset failed", e ) ); } },
        { text: "KEY HUNT QA: COMPLETE TASKS", action: () => { void KeyHuntService.debugComplete().then( () => { DebugConsoleManager.close(); DialogManager.closeAllDialogs(); KeyHuntDialog.open(); } ).catch( e => console.warn( "[KeyHunt] debug complete failed", e ) ); } },
        { text: "PRIZE SHOT", action: () => { DebugConsoleManager.close(); void ScreenManager.showScreen( Screens.GAME, { prize_shot: true } ); } },
        { text: "PRIZE SHOT QA: RESET TO START", action: () => resetPrizeShotToStart() },
        { text: "PRIZE SHOT QA: +3 BALLS", action: () => PrizeShotService.debugSetShots( PrizeShotService.shotsLeft() + 3 ) },
        { text: "PRIZE SHOT QA: +10 BALLS", action: () => PrizeShotService.debugSetShots( PrizeShotService.shotsLeft() + 10 ) },
        { text: "KEY HUNT QA: END PERIOD", action: () => { void KeyHuntService.debugExpire().catch( e => console.warn( "[KeyHunt] debug expire failed", e ) ); } },
        { text: "TOURNAMENT DIALOG", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new TournamentDialog( TournamentService.isFinished() ) ); } },
        { text: "TOURNAMENT DIALOG (FINISHED)", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new TournamentDialog( true ) ); } },
        { text: "TOURNAMENT +9 CROWNS", action: () => { TournamentService.addCrownsForDebug( 9 ); MenuScreen.active?.refreshTournamentProgress(); } },
        { text: "TOURNAMENT +10 CROWNS", action: () => { TournamentService.addCrownsForDebug( 10 ); MenuScreen.active?.refreshTournamentProgress(); } },
        { text: "TOURNAMENT +50 CROWNS", action: () => { TournamentService.addCrownsForDebug( 50 ); MenuScreen.active?.refreshTournamentProgress(); } },
        { text: "TOURNAMENT 0 CROWNS", action: () => { TournamentService.addCrownsForDebug( -TournamentService.getCrowns() ); MenuScreen.active?.refreshTournamentProgress(); } },
        { text: "TOURNAMENT SERVER CROWNS", action: () => { TournamentService.resetCrownsForDebug(); MenuScreen.active?.refreshTournamentProgress(); } },
        { text: "TOURNAMENT REFETCH", action: () => { void TournamentService.fetch( true ).then( () => console.log( "[tournament]", TournamentService.debugSummary() ) ); } },
        { text: "TOURNAMENT QA: +10 CROWNS ON SERVER", action: () => runTournamentCheat( "add-crowns", () => debugTournamentAddCrowns( 10 ) ) },
        { text: "TOURNAMENT QA: +100 CROWNS ON SERVER", action: () => runTournamentCheat( "add-crowns", () => debugTournamentAddCrowns( 100 ) ) },
        { text: "TOURNAMENT QA: FILL ROOM +5", action: () => runTournamentCheat( "fill-room", () => debugTournamentFillRoom( 5, 0, Math.max( 50, TournamentService.getCrowns() * 2 ) ) ) },
        { text: "TOURNAMENT QA: BOTS +30 MIN", action: () => runTournamentCheat( "advance-bots", () => debugTournamentAdvanceBots( 30 ) ) },
        { text: "TOURNAMENT QA: BOTS +3 HOURS", action: () => runTournamentCheat( "advance-bots", () => debugTournamentAdvanceBots( 180 ) ) },
        { text: "TOURNAMENT QA: RESET MY ENTRY", action: () => runTournamentCheat( "reset", () => debugTournamentReset() ) },
        { text: "TOURNAMENT WAIT DIALOG", action: () => { DebugConsoleManager.close(); TournamentWaitDialog.open(); } },
        { text: "TOURNAMENT REWARD OVERLAY", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.TOURNAMENT_REWARD_OVERLAY, { reward: TournamentService.getProgressRewards()[0] ?? { type: "fireworks", amount: 3 } } ); } },
        { text: "TOURNAMENT WIN OVERLAY", action: () => { DebugConsoleManager.close(); const place = TournamentService.getFinishedPlace() || 2; const rewards = TournamentService.getPlaceRewardsFor( place ); OverlayFactory.openOverlay( OverlayId.TOURNAMENT_WIN_OVERLAY, { place, rewards: rewards.length ? rewards : [ { type: "coin", amount: 250 }, { type: "fireworks", amount: 3 } ] } ); } },
        { text: "NO LEVELS", action: () => { DebugConsoleManager.close(); NoLevelsDialog.open(); } },
        { text: "RATE GAME", action: () => { DebugConsoleManager.close(); RateGameDialog.open(); } },
        { text: "GO STORE", action: () => { DebugConsoleManager.close(); GoStoreDialog.open(); } },
        { text: "UPDATE HARD", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new UpdateRequiredDialog( UpdateRequiredMode.Hard ) ); } },
        { text: "UPDATE SOFT", action: () => { DebugConsoleManager.close(); DialogManager.openDialog( new UpdateRequiredDialog( UpdateRequiredMode.Soft, 999 ) ); } },
        { text: "CONNECTION FAILED", action: () => {
          DebugConsoleManager.close();
          NetworkManager.handleNetworkError( new Error( "Debug connection failure" ) );
        } },
        { text: "LOADING ERROR", action: () => {
          DebugConsoleManager.close();
          NetworkManager.handleLoadingDataError( new Error( "Debug loading failure" ) );
        } },
        { text: "RESET RATE/UPDATE FLAGS", action: () => {
          StatisticsStorage.update( {
            rate_dialog_show_count: 0,
            rate_dialog_last_shown_level: 0,
            rate_dialog_completed: false,
          } );
          localStorage.removeItem( "update_soft_dismissed" );
          updateInfoText();
        } },
        { text: "INFINITE LIVES (15 MIN)", action: () => { DebugConsoleManager.close(); OverlayFactory.openOverlay( OverlayId.INFINITE_LIVES_OVERLAY, { duration_minutes: 15 } ); } },
      ],
    },
    {
      title: "MINE BOOSTER (STICKY BOMB)",
      buttons: [
        ...StickyMineShowcase.TESTS.map( ( test, index ) => ( {
          text: `${index + 1}. MINES: ${test.label}`,
          action: () =>
          {
            // The level starts with the Mine booster selected, as if picked in the Play dialog.
            if ( CumulativeBoosterService.inventory.getCount( CumulativeBoosterID.Mine ) <= 0 )
              CumulativeBoosterService.inventory.add( CumulativeBoosterID.Mine, 3 );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, {
              level: test.level,
              level_start_booster_id: CumulativeBoosterID.Mine,
            } );
          },
        } ) ),
        { text: "FIRE MINES NOW (CURRENT LEVEL, FREE)", action: () =>
          {
            const screen = activeGameScreen();
            if ( !screen )
            {
              console.warn( "[DebugConsole] Not currently in a game — open a level first" );
              return;
            }
            DebugConsoleManager.close();
            screen.debugLaunchStickyMines();
          } },
        { getText: () => `MINES INVENTORY: ${CumulativeBoosterService.inventory.getCount( CumulativeBoosterID.Mine )} (+3)`,
          action: () => { CumulativeBoosterService.inventory.add( CumulativeBoosterID.Mine, 3 ); } },
      ],
    },
    {
      title: "BRICK TEST LEVELS",
      buttons: BrickLevelsShowcase.TESTS.map( ( test, index ) => ( {
        text: `${index + 1}. ${test.label}`,
        action: () =>
        {
          DebugConsoleManager.close();
          Game.instance.hideLoadingGameScreen();
          void ScreenManager.showScreen( Screens.GAME, { level: test.level } );
        },
      } ) ),
    },
    {
      title: "BLOCKS (SHOWCASE)",
      buttons: [
        ...NewBlockModelsShowcase.TESTS.map( ( test, index ) => ( {
          text: `NEW MODEL: ${test.label}`,
          action: () =>
          {
            const level = NewBlockModelsShowcase.activate( index );
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          },
        } ) ),
        { getText: () => `ALL BLOCKS (${BlockShowcase.blockCount()})`, action: () =>
          {
            const level = BlockShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { getText: () => `BLOCK NAMES: ${BlockShowcase.labelModeName()}`,
          action: () => BlockShowcase.cycleLabels() },
        ...[ "column", "rotating_table", "side_to_side_table", "up_and_down_table", "tilted_table", "fan", "hammer" ].map( label => ( {
          text: `NEW GOAL: ${label.toUpperCase()}`,
          action: () =>
          {
            DebugConsoleManager.close();
            OverlayFactory.openOverlay( OverlayId.NEW_GOAL_OVERLAY, { unlocks: [ label ] } );
          },
        } ) ),
      ],
    },
    {
      title: "BENCH LEVELS",
      buttons: [
        { text: "TILTED TABLE LEVEL (tiltDeg test)", action: () =>
          {
            const level = TiltedTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "ROTATED TABLE LEVEL (diamond pyramid 5-1)", action: () =>
          {
            const level = RotatedTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "SHELF TABLE LEVEL (standOn test)", action: () =>
          {
            const level = ShelfTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "HANGING TABLE LEVEL (no-leg swing test)", action: () =>
          {
            const level = HangingTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "ELEVATOR TABLE LEVEL (up/down leg test)", action: () =>
          {
            const level = ElevatorTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "SLIDE TABLE LEVEL (left/right deck test)", action: () =>
          {
            const level = SlideTableShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "LEANING LOGS LEVEL (teepee test)", action: () =>
          {
            const level = LeaningLogsShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "TABLE CASE LEVEL (scratch bench)", action: () =>
          {
            const level = TableCaseShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR BAY LEVEL (posts + bars + discs)", action: () =>
          {
            const level = JarBayShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR SANDWICH LEVEL (posts + discs)", action: () =>
          {
            const level = JarSandwichShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR CASES LEVEL (collider bench)", action: () =>
          {
            const level = JarCasesShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAR PYRAMID LEVEL", action: () =>
          {
            const level = JarPyramidShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "DIAMOND TOWER LEVEL (rotationDeg test)", action: () =>
          {
            const level = DiamondTowerShowcase.activate();
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level } );
          } },
        { text: "JAM SPLASH TEST", action: () =>
          {
            DebugConsoleManager.close();
            const camera = SceneManager.camera;
            const target = camera.getWorldDirection( new THREE.Vector3() )
              .multiplyScalar( 14 )
              .add( camera.position );
            Vfx3DManager.addJamSplash( target, 0.5, 1, 0 );
            const jam = Vfx3DRuntime.getBatch( VfxSplashBatch );
            const glass = Vfx3DRuntime.getBatch( VfxShardBatch );
            for ( const at of [ 100, 400, 800 ] )
            {
              setTimeout( () => console.log(
                `[JAM TEST ${at}ms] jam=${jam.debugInstanceCount()} glass=${glass.debugInstanceCount()}`
                + ` vfxtime=${Vfx3DRuntime.current_time.toFixed( 2 )}`
              ), at );
            }
          } },
        { text: "EMPTY LEVEL", action: () =>
          {
            GlobalSettings.DEBUG_EMPTY_LEVEL = true;
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.GAME, { level: StatisticsStorage.load().level } );
          } },
      ],
    },
    {
      title: "TEXTURE TEST (ONE BLOCK PER LEVEL)",
      buttons: [
        { getText: () => `RUN #${TextureTest.getIndex() + 1}/${TextureTest.COUNT}: ${TextureTest.getName()}`,
          action: () => runTextureLevel( TextureTest.getIndex() ) },
        { getText: () => `NEXT  →  #${( TextureTest.getIndex() + 1 ) % TextureTest.COUNT + 1}: ${TextureTest.getName( TextureTest.getIndex() + 1 )}`,
          action: () => runTextureLevel( TextureTest.getIndex() + 1 ) },
        { getText: () => `PREV  ←  #${( TextureTest.getIndex() - 1 + TextureTest.COUNT ) % TextureTest.COUNT + 1}: ${TextureTest.getName( TextureTest.getIndex() - 1 )}`,
          action: () => runTextureLevel( TextureTest.getIndex() - 1 ) },
        { text: "FROM THE FIRST TEXTURE", action: () => runTextureLevel( 0 ) },
        { getText: () => `TEXTURE MODE: ${TextureTest.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( TextureTest.isActive() )
            {
              TextureTest.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runTextureLevel( TextureTest.getIndex() );
            }
          } },
      ],
    },
    {
      title: "TABLE LAYOUTS (2-4 TABLES, EVERY FORMATION)",
      buttons: [
        { getText: () => `RUN #${TableFormationShowcase.getIndex() + 1}/${TableFormationShowcase.COUNT}: ${TableFormationShowcase.getName()}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() ) },
        { getText: () => `NEXT  →  #${( TableFormationShowcase.getIndex() + 1 ) % TableFormationShowcase.COUNT + 1}: ${TableFormationShowcase.getName( TableFormationShowcase.getIndex() + 1 )}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() + 1 ) },
        { getText: () => `PREV  ←  #${( TableFormationShowcase.getIndex() - 1 + TableFormationShowcase.COUNT ) % TableFormationShowcase.COUNT + 1}: ${TableFormationShowcase.getName( TableFormationShowcase.getIndex() - 1 )}`,
          action: () => runTableFormationLevel( TableFormationShowcase.getIndex() - 1 ) },
        { text: "FROM THE FIRST LAYOUT", action: () => runTableFormationLevel( 0 ) },
        { getText: () => `LAYOUT MODE: ${TableFormationShowcase.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( TableFormationShowcase.isActive() )
            {
              TableFormationShowcase.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runTableFormationLevel( TableFormationShowcase.getIndex() );
            }
          } },
      ],
    },
    {
      title: "ЛАЙН С ПРЕГРАДАМИ (BARRIER LINE)",
      buttons: [
        { text: `▶ ЗАПУСТИТЬ ЛАЙН С #1 (${BarrierShowcase.LINE.length} УРОВНЕЙ)`, action: () => runBarrierLine( 0 ) },
        { getText: () => `ПРОДОЛЖИТЬ #${BarrierShowcase.getLineIndex() + 1}: ${BarrierShowcase.getLineLabel( BarrierShowcase.getLineIndex() )}`,
          action: () => runBarrierLine( BarrierShowcase.getLineIndex() ) },
        { getText: () => `СЛЕДУЮЩИЙ  →  #${( BarrierShowcase.getLineIndex() + 1 ) % BarrierShowcase.LINE.length + 1}: ${BarrierShowcase.getLineLabel( BarrierShowcase.getLineIndex() + 1 )}`,
          action: () => runBarrierLine( BarrierShowcase.getLineIndex() + 1 ) },
      ],
    },
    {
      title: "BARRIERS",
      buttons: BarrierShowcase.TESTS.map( ( test, index ) => ( {
        text: `BARRIER: ${test.label}`,
        action: () =>
        {
          const level = BarrierShowcase.activate( index );
          DebugConsoleManager.close();
          Game.instance.hideLoadingGameScreen();
          void ScreenManager.showScreen( Screens.GAME, { level } );
        },
      } ) ),
    },
    ...( [
      [ "COLUMN STRESS (FPS / TRIS / DRAW CALLS)", "column" ],
      [ "CAN STRESS (FPS / TRIS / DRAW CALLS)", "can" ],
    ] as const ).map( ( [ title, group ] ) => ( {
      title,
      buttons: [
        ...StressTest.indicesOf( group ).map( index => ( {
          getText: () => `${StressTest.getName( index )}  (${StressTest.getBlockCount( index )})`,
          action: () => runStressLevel( index ),
        } ) ),
        { getText: () => `AUTO FIRE: ${StressTest.isAutofiring() ? "ON" : "OFF"}`,
          action: () =>
          {
            StressTest.toggleAutofire();
            DebugConsoleManager.close();
          } },
      ],
    } ) ),
    {
      title: "STRESS TEST (FPS)",
      buttons: [
        { getText: () => `RUN #${StressTest.getIndex() + 1}/${StressTest.COUNT}: ${StressTest.getName()}`,
          action: () => runStressLevel( StressTest.getIndex() ) },
        { getText: () => `BLOCKS: ${StressTest.getBlockCount()}  →  NEXT #${( StressTest.getIndex() + 1 ) % StressTest.COUNT + 1}`,
          action: () => runStressLevel( StressTest.getIndex() + 1 ) },
        { text: "REPLAY CURRENT", action: () => runStressLevel( StressTest.getIndex() ) },
        { text: "▶ BRICK vs PLY BENCH (~1.5 MIN, REPORT ON SCREEN)", action: () =>
          {
            DebugConsoleManager.close();
            void BrickBench.runAndShow();
          } },
        { text: "BRICK vs PLY: SHOW LAST REPORT", action: () =>
          {
            DebugConsoleManager.close();
            BrickBench.showLast();
          } },
        { getText: () => `AUTO FIRE: ${StressTest.isAutofiring() ? "ON" : "OFF"}`,
          action: () =>
          {
            StressTest.toggleAutofire();
            DebugConsoleManager.close();
          } },
        { text: "RUN + AUTO FIRE", action: () =>
          {
            StressTest.setAutofire( true );
            runStressLevel( StressTest.getIndex() );
          } },
        { getText: () => `STRESS MODE: ${StressTest.isActive() ? "ON" : "OFF"}`,
          action: () =>
          {
            if ( StressTest.isActive() )
            {
              StressTest.deactivate();
              DebugConsoleManager.close();
              void ScreenManager.showScreen( Screens.MENU );
            }
            else
            {
              runStressLevel( StressTest.getIndex() );
            }
          } },
      ],
    },
    {
      title: "TRIANGLE BENCH (ПУСТАЯ СЦЕНА)",
      buttons: [
        { text: "ОТКРЫТЬ СТЕНД ТРЕУГОЛЬНИКОВ", action: () =>
          {
            DebugConsoleManager.close();
            Game.instance.hideLoadingGameScreen();
            void ScreenManager.showScreen( Screens.MENU ).then( () => TriangleBench.open() );
          } },
      ],
    },
    {
      title: "ЗАДНИЙ СВЕТ (RIM)",
      buttons: [
        { getText: () => `РЕЖИМ: ${RimLightTest.modeLabel()}`,
          action: () => RimLightTest.cycleMode() },
        { getText: () => `ЦВЕТ: ${RimLightTest.modeLabel()}`,
          action: () => RimLightTest.cycleColor() },
        { text: "СБРОС ЗАДНЕГО СВЕТА", action: () => RimLightTest.reset() },
      ],
    },
    {
      title: "ЯДРО (СИЛА / МАССА)",
      buttons: [
        { getText: () => `ПАНЕЛЬ ЯДРА: ${BallTuner.stateLabel()}`, action: () =>
          {
            DebugConsoleManager.close();
            BallTuner.open();
          } },
        { text: "СБРОСИТЬ ЯДРО", action: () => BallTuner.reset() },
      ],
    },
    {
      title: "МАССЫ БЛОКОВ (BLOCK MASS)",
      buttons: [
        { getText: () => `МАССЫ БЛОКОВ → ДЕФОЛТ (${BlockMassTuner.modifiedCount()})`,
          action: () => TextureTest.resetMasses() },
      ],
    },
    {
      title: "MATERIAL INSPECTOR (РЕГУЛИРОВКА БЛОКОВ)",
      buttons: [
        { text: "ОТКРЫТЬ ИНСПЕКТОР МАТЕРИАЛОВ", action: () =>
          {
            DebugConsoleManager.close();
            void openMaterialInspector();
          } },
        { text: "СБРОСИТЬ ВСЁ", action: () => ( window as any ).__inspector?.reset() },
        { text: "ВЫГРУЗИТЬ ИНСПЕКТОР", action: () => ( window as any ).__inspector?.remove() },
      ],
    },
    {
      title: "PERF DIAG (RENDER / PHYSICS)",
      buttons: [
        { getText: () => `GRAPHICS: ${GraphicsSettings.get().toUpperCase()}`, action: () =>
          {
            const next = ( GRAPHICS_QUALITY_ORDER.indexOf( GraphicsSettings.get() ) + 1 ) % GRAPHICS_QUALITY_ORDER.length;
            GraphicsSettings.set( GRAPHICS_QUALITY_ORDER[next] );
          } },
        { getText: () => `SHADOWS: ${Renderer.renderer?.shadowMap.enabled ? "ON" : "OFF"}`, action: () => diagToggleShadows() },
        { getText: () => `RENDER SCALE: ${diag_render_scales[diag_scale_index].toFixed( 2 )}x`, action: () => diagCycleRenderScale() },
        { getText: () => `SOLVER ITERS: ${diag_solver_iters}`, action: () => diagToggleSolverIters() },
        { getText: () => `MSAA: ${localStorage.getItem( "perf_diag_no_msaa" ) === "1" ? "OFF" : "ON"} (RELOAD)`, action: () =>
          {
            localStorage.setItem( "perf_diag_no_msaa", localStorage.getItem( "perf_diag_no_msaa" ) === "1" ? "0" : "1" );
            window.location.reload();
          } },
        { getText: () => `UI BACKEND: ${GlobalSettings.UI_BACKEND.toUpperCase()} (RELOAD)`, action: () =>
          {
            setUiBackendOverride( GlobalSettings.UI_BACKEND === "dom" ? "canvas" : "dom" );
            window.location.reload();
          } },
        { getText: () => `SHADOW: ${SHADOW_PRESETS[diag_shadow_index].label}`, action: () => diagCycleShadowQuality() },
        { getText: () => `RECV SHADOWS: ${diag_receive_shadows ? "ON" : "OFF"}`, action: () => diagToggleReceiveShadows() },
        { getText: () => `CCD: ${diag_ccd ? "ON" : "OFF"}`, action: () => diagToggleCcd() },
        { getText: () => `FRONT LIGHT: ${GlobalSettings.DEBUG_NO_FRONT_LIGHT ? "OFF" : "ON"}`, action: () => diagToggleFrontLight() },
        { getText: () => `LIGHTS: ${LIGHT_MODES[diag_light_mode]}`, action: () => diagCycleLights() },
        { getText: () => `MATERIALS: ${MATERIAL_MODES[diag_material_mode]}`, action: () => diagCycleMaterials() },
        { getText: () => `CLEARCOAT: ${PixelFeatures.clearcoat ? "ON" : "OFF"}`, action: () => diagToggleClearcoat() },
        { getText: () => `NORMAL MAPS: ${PixelFeatures.normal_maps ? "ON" : "OFF"}`, action: () => diagToggleNormalMaps() },
        { getText: () => `EMISSIVE MAPS: ${PixelFeatures.emissive_maps ? "ON" : "OFF"}`, action: () => diagToggleEmissiveMaps() },
        { getText: () => `BALL SHADOW: ${CannonBall.cast_shadow ? "ON" : "OFF"}`, action: () => { CannonBall.cast_shadow = !CannonBall.cast_shadow; } },
        { getText: () => `VFX 3D: ${GlobalSettings.DEBUG_DISABLE_VFX3D ? "OFF" : "ON"}`, action: () =>
          {
            GlobalSettings.DEBUG_DISABLE_VFX3D = !GlobalSettings.DEBUG_DISABLE_VFX3D;
            if ( GlobalSettings.DEBUG_DISABLE_VFX3D )
              Vfx3DRuntime.killAllBatches();
          } },
      ],
    },
    {
      title: "DEBUG OVERLAYS",
      buttons: [
        { getText: () => `MEM OVERLAY: ${MemoryOverlay.isVisible() ? "ON" : "OFF"}`, action: () => MemoryOverlay.toggle() },
        { getText: () => `RAM DASHBOARD: ${MemoryDashboard.isVisible() ? "ON" : "OFF"}`, action: () => MemoryDashboard.toggle() },
        { getText: () => `FPS OVERLAY: ${GlobalSettings.SHOW_FPS_OVERLAY ? "ON" : "OFF"}`, action: () => Game.instance.setFpsOverlayEnabled( !GlobalSettings.SHOW_FPS_OVERLAY ) },
        { getText: () => `LOG OVERLAY: ${LogOverlay.isVisible() ? "ON" : "OFF"}`, action: () => LogOverlay.toggle() },
        { getText: () => `UI PERF PROBE: ${UiPerfProbe.isPanelVisible() ? "ON" : "OFF"}`, action: () => UiPerfProbe.togglePanel() },
        { getText: () => `PERF SESSION: ${PerfSession.isRecording() ? "RECORDING (TAP TO STOP)" : "OFF (TAP TO START)"}`, action: () => PerfSession.toggle() },
        { getText: () => `PERF SESSION AUTOSTART: ${PerfSession.isAutostart() ? "ON" : "OFF"}`, action: () => PerfSession.setAutostart( !PerfSession.isAutostart() ) },
        { text: "PERF SESSION: LAST REPORT", action: () => PerfSession.showPanel() },
        { text: "PERF SESSION: SAVE TXT", action: () => { void PerfSession.save( true ); } },
        { getText: () => `PHYSICS COLLIDERS: ${PhysicsDebugRenderer.isEnabled() ? "ON" : "OFF"}`, action: () => PhysicsDebugRenderer.setEnabled( !PhysicsDebugRenderer.isEnabled() ) },
        { text: "HAPTICS TEST (LIGHT/MED/HEAVY)", action: () =>
          {
            void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );
            setTimeout( () => void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL ), 400 );
            setTimeout( () => void hapticsController.vibrate( HapticPattern.HEAVY, VibrationContext.GENERAL ), 800 );
          } },
      ],
    },
    {
      title: "PLAYER",
      buttons: [
        { text: "NEW USER", action: () => createNewUserAndReload() },
        { getText: () => reset_progress_inflight ? "RESETTING PROGRESS..." : "RESET PROGRESS", action: () => { void resetProgressOnServer(); } },
        { getText: () => reset_adventure_inflight ? "RESETTING ADVENTURE..." : "RESET ADVENTURE", action: () => { void resetAdventureOnServer(); } },
        { getText: () => {
          const stats = StatisticsStorage.load();
          return add_heart_inflight
            ? "ADDING HEART..."
            : `HEART +1 (${stats.hearts}/${stats.max_hearts})`;
        }, action: () => { void addHeartOnServer(); } },
      ],
    },
    {
      title: "BOOSTERS (GRANT TO SELF)",
      buttons: [
        { getText: () => `ROCKET (FIREWORKS) ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.Fireworks ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.Fireworks ) },
        { getText: () => `MULTIBALL ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.Multiball ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.Multiball ) },
        { getText: () => `BIG BALL ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.BigBall ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.BigBall ) },
        { getText: () => `INFINITE BALLS ×1  →  have ${activeGameScreen()?.getBoosterCount( CumulativeBoosterID.InfiniteBalls ) ?? "?"}`,
          action: () => grantBoosterUse( CumulativeBoosterID.InfiniteBalls ) },
      ],
    },
    {
      title: "OFFERS",
      buttons: [
        { getText: () => {
          const placement = OfferService.getPlacementOffer( ONE_PLUS_ONE_PLACEMENT );
          return placement ? `OPEN 1+1 OFFER (${placement.offer.id})` : "OPEN 1+1 OFFER (DEBUG PREVIEW)";
        }, action: () => {
          openDebugOnePlusOffer( ONE_PLUS_ONE_PLACEMENT, Screens.ONE_PLUS_ONE_OFFER );
        } },
        { getText: () => {
          const placement = OfferService.getPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
          return placement ? `OPEN 1+2 OFFER (${placement.offer.id})` : "OPEN 1+2 OFFER (DEBUG PREVIEW)";
        }, action: () => {
          openDebugOnePlusOffer( ONE_PLUS_TWO_PLACEMENT, Screens.ONE_PLUS_TWO_OFFER );
        } },
        { getText: () => {
          const offer = OfferService.getChainOffers( 'chain_video' )[0];
          return offer ? `OPEN ADS CHAIN OFFER (${offer.id})` : "OPEN ADS CHAIN OFFER (none resolved)";
        }, action: () => {
          if ( !OfferService.getChainOffers( 'chain_video' ).length ) return;
          DebugConsoleManager.close();
          void ScreenManager.showScreen( Screens.CHAIN_VIDEO );
        } },
        { getText: () => reset_offer_inflight ? "RESETTING..." : "RESET 1+1 / 1+2 PROGRESS", action: () => { void resetOnePlusOneProgress(); } },
        ...( OfferService.isPurchaseSimulationAvailable() ? [
          {
            getText: () => `SIMULATE PURCHASE: ${OfferService.isPurchaseSimulationEnabled() ? "ON" : "OFF"} (TEST)`,
            action: () => OfferService.setPurchaseSimulationEnabled( !OfferService.isPurchaseSimulationEnabled() ),
          },
        ] : [] ),
      ],
    },
    {
      title: "ADS (APPLOVIN MAX)",
      buttons: [
        { text: "MAX MEDIATION DEBUGGER", action: () => { DebugConsoleManager.close(); void MaxAds.showMediationDebugger(); } },
        { text: "SHOW INTERSTITIAL", action: () => { DebugConsoleManager.close(); void MaxAds.tryShowInterstitial( "debug", 1 ); } },
        { text: "SHOW REWARDED", action: () => { DebugConsoleManager.close(); void MaxAds.tryShowRewarded( "debug", 1 ); } },
      ],
    },
    {
      title: "NOTIFICATIONS (fire in ~1s, leave app)",
      buttons: [
        { text: "TEST 1: LOGO",        action: () => { void NotificationManager.debugTest1LargeIconOnly(); } },
        { text: "TEST 2: BANNER",      action: () => { void NotificationManager.debugTest2BigPictureOnly(); } },
        { text: "TEST 3: LOGO+BANNER", action: () => { void NotificationManager.debugTest3DifferentImages(); } },
        { text: "TEST 4: HIDE LOGO",   action: () => { void NotificationManager.debugTest4HideLogo(); } },
        { text: "TEST 5: NO IMAGE",    action: () => { void NotificationManager.debugTest5NoImage(); } },
        { text: "SESSION PUSH NOW",    action: () => { void NotificationManager.debugTestSessionPushNow(); } },
        { text: "RESCHEDULE ALL",      action: () => { void NotificationManager.onForeground().then( refreshNotificationsDebugText ); } },
        { text: "CANCEL ALL",          action: () => { void NotificationManager.debugCancelAll().then( refreshNotificationsDebugText ); } },
        { text: "SHOW PENDING",        action: () => { void refreshNotificationsDebugText(); } },
      ],
    },
    {
      title: "S2S (META / GOOGLE / APPSFLYER)",
      buttons: [
        { getText: () => `S2S STATUS (queue ${S2SEventOutbox.list().length})`, action: () => refreshS2SDebugText() },
        { text: "S2S: RETRY QUEUE NOW", action: () => { CustomEventsController.retryOutboxNow(); window.setTimeout( refreshS2SDebugText, 1500 ); } },
        ...( GlobalSettings.IS_PROD ? [] : [ {
          text: "S2S: SEND TEST tutorial_complete",
          action: () =>
          {
            const event_id = CustomEventsController.queueS2SEvent( "tutorial_complete", { tutorial_version: "debug", debug: true } );
            console.info( `[S2S] debug tutorial_complete queued: ${event_id ?? "rejected"}` );
            window.setTimeout( refreshS2SDebugText, 1500 );
          },
        } ] ),
      ],
    },
    {
      title: "ENVIRONMENT",
      buttons: [
        { getText: () => `SERVER ENV: ${getRuntimeEnvironmentLabel()}`, action: () => toggleRuntimeEnvironment() },
        { text: "CHECK SERVER ROUTING", action: () => void checkServerRouting() },
        { getText: () => `CLEAR ROUTING OVERRIDE${getServerRoutingOverride() ? "" : " (none)"}`, action: () => clearServerRoutingOverride() },
      ],
    },
  ];

  // The console contains tools with very different purposes. Five concise
  // pages keep the touch targets useful without hiding any existing cheat.

  const page = ( ...titles: string[] ): SectionConfig[] =>
    titles.flatMap( title => sections.filter( section => section.title === title ) );

  const pages: DebugPage[] = [
    { title: "PLAY", sections: page( "SCREENS", "LEVEL", "AUDIO", "LOCALIZATION", "FEATURE TUTORIALS" ) },
    { title: "UI", sections: page( "RESULT OVERLAY", "DIALOGS" ) },
    { title: "TEST", sections: page( "MINE BOOSTER (STICKY BOMB)", "BRICK TEST LEVELS", "ЛАЙН С ПРЕГРАДАМИ (BARRIER LINE)", "BLOCKS (SHOWCASE)", "BENCH LEVELS", "TEXTURE TEST (ONE BLOCK PER LEVEL)", "TABLE LAYOUTS (2-4 TABLES, EVERY FORMATION)", "BARRIERS", "COLUMN STRESS (FPS / TRIS / DRAW CALLS)", "CAN STRESS (FPS / TRIS / DRAW CALLS)", "STRESS TEST (FPS)" ) },
    { title: "TUNE", sections: page( "TRIANGLE BENCH (ПУСТАЯ СЦЕНА)", "ЗАДНИЙ СВЕТ (RIM)", "ЯДРО (СИЛА / МАССА)", "МАССЫ БЛОКОВ (BLOCK MASS)", "MATERIAL INSPECTOR (РЕГУЛИРОВКА БЛОКОВ)", "PERF DIAG (RENDER / PHYSICS)", "DEBUG OVERLAYS" ) },
    { title: "ACCOUNT", sections: page( "PLAYER", "BOOSTERS (GRANT TO SELF)", "OFFERS", "ADS (APPLOVIN MAX)", "NOTIFICATIONS (fire in ~1s, leave app)", "S2S (META / GOOGLE / APPSFLYER)", "ENVIRONMENT" ) },
  ];
  const placed = new Set( pages.flatMap( item => item.sections ) );
  pages[pages.length - 1].sections.push( ...sections.filter( item => !placed.has( item ) ) );
  active_debug_page = Math.min( active_debug_page, pages.length - 1 );

  const tabs = document.createElement( "div" );
  tabs.style.display = "grid";
  tabs.style.gridTemplateColumns = "repeat(5, minmax(0, 1fr))";
  tabs.style.gap = "6px";
  tabs.style.marginBottom = "16px";
  overlay.appendChild( tabs );

  const page_content = document.createElement( "div" );
  overlay.appendChild( page_content );

  const renderPage = (): void =>
  {
    dynamic_buttons = [];
    tabs.replaceChildren();
    page_content.replaceChildren();
    for ( const [index, page] of pages.entries() )
    {
      const tab = document.createElement( "button" );
      tab.type = "button";
      tab.textContent = page.title;
      styleButton( tab, index === active_debug_page ? "#2e7d32" : "#385090" );
      tab.style.padding = "12px 4px";
      tab.addEventListener( "click", () =>
      {
        active_debug_page = index;
        renderPage();
        updateInfoText();
      } );
      tabs.appendChild( tab );
    }
    for ( const section of pages[active_debug_page].sections )
      page_content.appendChild( buildSection( section ) );
  };
  renderPage();

  info_el = document.createElement( "pre" );
  info_el.style.marginTop = "16px";
  info_el.style.padding = "10px";
  info_el.style.background = "rgba(255,255,255,0.06)";
  info_el.style.borderRadius = "8px";
  info_el.style.whiteSpace = "pre-wrap";
  info_el.style.font = "12px monospace";
  overlay.appendChild( info_el );

  return overlay;
}

function buildSection( section: SectionConfig ): HTMLDivElement
{
  const wrap = document.createElement( "div" );
  wrap.style.marginBottom = "18px";

  const title = document.createElement( "div" );
  title.textContent = section.title;
  title.style.font = "700 13px Arial, sans-serif";
  title.style.opacity = "0.7";
  title.style.marginBottom = "8px";
  title.style.letterSpacing = "1px";
  wrap.appendChild( title );

  const grid = document.createElement( "div" );
  grid.style.display = "grid";
  grid.style.gridTemplateColumns = "repeat(auto-fill, minmax(150px, 1fr))";
  grid.style.gap = "8px";

  for ( const cfg of section.buttons )
  {
    const btn = document.createElement( "button" );
    btn.type = "button";
    btn.textContent = cfg.getText ? cfg.getText() : ( cfg.text ?? "" );
    styleButton( btn, "#385090" );
    btn.addEventListener( "click", () =>
    {
      cfg.action();
      updateInfoText();
    } );
    grid.appendChild( btn );

    if ( cfg.getText )
      dynamic_buttons.push( { btn, cfg } );
  }

  wrap.appendChild( grid );
  return wrap;
}

function styleButton( btn: HTMLButtonElement, background: string ): void
{
  btn.style.padding = "10px 12px";
  btn.style.borderRadius = "8px";
  btn.style.border = "none";
  btn.style.background = background;
  btn.style.color = "#fff";
  btn.style.font = "700 12px Arial, sans-serif";
  btn.style.cursor = "pointer";
}

function updateInfoText(): void
{
  for ( const { btn, cfg } of dynamic_buttons )
    if ( cfg.getText ) btn.textContent = cfg.getText();

  if ( !info_el ) return;

  const screen = ScreenManager.getCurrentScreen() ?? "none";
  const stats = StatisticsStorage.load();

  const routing = getServerRoutingOverride();
  const routing_result = ServerRoutingService.getLastResult();
  info_el.textContent =
    `Env: ${GlobalSettings.ENV}\n` +
    `Branch: ${GIT_BRANCH}\n` +
    `API: ${GlobalSettings.API_BASE_URL}${routing ? " (routed)" : ""}\n` +
    `Device: ${localStorage.getItem( "device_id" ) ?? "?"}\n` +
    `Routing: ${routing_result ? ( routing_result.error ?? ( routing_result.changed ? "changed" : "ok" ) ) : "not checked"}` +
    `${routing_result?.rule_id ? ` (rule ${routing_result.rule_id} → ${routing_result.server_key})` : ""}${routing_result?.transfer && routing_result.transfer !== "none" ? `, transfer ${routing_result.transfer}` : ""}\n` +
    `Screen: ${screen}\n` +
    `Level: ${stats.level}\n` +
    `Hearts: ${stats.hearts}/${stats.max_hearts}\n` +
    `No ads until: ${stats.no_ads_until > Date.now() ? `${Math.ceil( ( stats.no_ads_until - Date.now() ) / 60_000 )} min` : "-"}\n` +
    `Sounds: ${stats.sounds_enabled ? "ON" : "OFF"}\n` +
    `Music: ${stats.music_enabled ? "ON" : "OFF"}` +
    ( notifications_debug_text ? `\n\nNotifications:\n${notifications_debug_text}` : "" ) +
    ( s2s_debug_text ? `\n\nS2S:\n${s2s_debug_text}` : "" );
}

async function refreshNotificationsDebugText(): Promise<void>
{
  try
  {
    notifications_debug_text = await NotificationManager.debugDescribePending();
  }
  catch ( error )
  {
    notifications_debug_text = `error: ${String( error )}`;
  }
  updateInfoText();
}

function cycleLanguage(): void
{
  const langs = [ ...new Set( LocalizationManager.getAvailableLanguages() ) ];
  const current_index = langs.indexOf( LocalizationManager.getCurrentLanguage() );
  const next_lang = langs[ ( current_index + 1 ) % langs.length ];
  LocalizationManager.setLanguage( next_lang );
}

/**
 * Moves campaign progress. player_level is owned by the server and only ever
 * advances through level-complete, so writing it into local storage alone is
 * wiped by the next player sync — the round trip is what makes a cheat stick.
 */
async function setLevelOnServer( level: number ): Promise<void>
{
  if ( set_level_inflight )
    return;

  const target = Math.trunc( level );
  if ( !Number.isFinite( target ) || target < 1 )
    return;

  // Landing on a level the campaign does not have leaves the menu with nothing
  // to show and no way back except another cheat.
  const max_level = getMaxCampaignLevel();
  if ( max_level > 0 && target > max_level )
  {
    window.alert( `No such level — the campaign ends at ${ max_level }.` );
    return;
  }

  if ( target === StatisticsStorage.load().level )
    return;

  set_level_inflight = true;
  updateInfoText();

  try
  {
    const response = await debugSetLevel( target );
    adoptEconomyFromPlayer( response.player );
    // adoptEconomyFromPlayer carries the economy across but not these two.
    // last_menu_level is what the menu drives the cart from, and a debug jump
    // should land on the level rather than animate through everything in
    // between — REPLAY CART DRIVE is there when the animation is the point.
    StatisticsStorage.update( {
      seen_level: response.player.last_seen_level ?? target,
      last_menu_level: target,
    } );

    // The menu reads the level once, while it is being built, so without a
    // rebuild it keeps showing the old one — which is a large part of why the
    // old local-only cheat looked like it did nothing. Awaiting inside the
    // inflight guard also serialises repeated taps.
    if ( ScreenManager.getCurrentScreen() === Screens.MENU )
      await ScreenManager.showScreen( Screens.MENU );
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to set level on server", error );
    window.alert( "Failed to set level on the server — see console for details." );
  }
  finally
  {
    set_level_inflight = false;
    updateInfoText();
  }
}

async function resetOnePlusOneProgress(): Promise<void>
{
  if ( reset_offer_inflight ) return;
  const placement = OfferService.getPlacementOffer( ONE_PLUS_ONE_PLACEMENT ) ?? OfferService.getPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
  const offer_id = placement?.offer.id ?? window.prompt( "No 1+1 / 1+2 placement resolved. Offer id to reset:", "" );
  if ( !offer_id ) return;
  reset_offer_inflight = true;
  try
  {
    const result = await debugResetOfferState( offer_id );
    await OfferService.refresh();
    window.alert( result.reset ? `1+1 progress for "${offer_id}" reset` : `No progress stored for "${offer_id}"` );
  }
  catch ( error )
  {
    window.alert( `Reset failed: ${error instanceof Error ? error.message : String( error )}` );
  }
  finally
  {
    reset_offer_inflight = false;
  }
}

function openDebugOnePlusOffer( placement_id: string, screen: Screens.ONE_PLUS_ONE_OFFER | Screens.ONE_PLUS_TWO_OFFER ): void
{
  const resolved = OfferService.getPlacementOffer( placement_id );
  DebugConsoleManager.close();

  if ( resolved )
  {
    void ScreenManager.showScreen( Screens.MENU ).then( () => openPlacementOffer( placement_id ) );
    return;
  }

  const offer = createDebugOnePlusOffer( placement_id );
  void ScreenManager.showScreen( screen, { offer, placement_id } );
}

function createDebugOnePlusOffer( placement_id: string ): OfferDto
{
  const is_one_plus_two = placement_id === ONE_PLUS_TWO_PLACEMENT;
  const template = is_one_plus_two ? ONE_PLUS_TWO_TEMPLATE : ONE_PLUS_ONE_TEMPLATE;
  const paid_rewards = { coins: 1000, hearts: 3, big_balls: 1, infinite_balls: 1 };
  const free_rewards = { coins: 1000, multi_balls: 1, fireworks: 1, infinite_hearts_minutes: 300 };
  const steps = [
    {
      id: "debug_paid",
      free: false,
      rewards: paid_rewards,
      products: {
        android: { productId: "debug.one_plus" },
        ios: { productId: "debug.one_plus" },
      },
    },
    { id: "debug_free_1", free: true, rewards: free_rewards },
    ...( is_one_plus_two
      ? [ { id: "debug_free_2", free: true, rewards: { coins: 500, hearts: 2, fireworks: 2, infinite_balls: 2 } } ]
      : [] ),
  ];

  return {
    id: `debug_${template}`,
    entitlement_key: `debug_${template}`,
    enabled: true,
    priority: 0,
    presentation: "placement_dialog",
    products: {},
    ends_at: new Date( Date.now() + 48 * 60 * 60 * 1000 ).toISOString(),
    placement: { id: placement_id, template, min_level: 0 },
    chain: { steps, purchases: [], next_step_id: "debug_paid" },
  };
}

function shiftLevelOnServer( delta: number ): Promise<void>
{
  return setLevelOnServer( StatisticsStorage.load().level + delta );
}

function setLevelPrompt(): Promise<void> | void
{
  const current = StatisticsStorage.load().level;
  const input = window.prompt( "Set progress to level:", String( current ) );
  if ( input === null )
    return;

  const level = Math.trunc( Number( input ) );
  if ( !Number.isFinite( level ) || level < 1 )
  {
    window.alert( "Enter a valid level number" );
    return;
  }

  return setLevelOnServer( level );
}

async function addHeartOnServer(): Promise<void>
{
  if ( add_heart_inflight )
    return;

  const stats = StatisticsStorage.load();
  if ( stats.hearts >= stats.max_hearts )
    return;

  add_heart_inflight = true;
  updateInfoText();

  try
  {
    const response = await rewardHeart();
    adoptEconomyFromPlayer( response.player );
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to add heart on server", error );
  }
  finally
  {
    add_heart_inflight = false;
    updateInfoText();
  }
}

async function resetProgressOnServer(): Promise<void>
{
  if ( reset_progress_inflight )
    return;

  if ( !window.confirm( "Reset all game progress locally and on the server? This cannot be undone." ) )
    return;

  reset_progress_inflight = true;
  updateInfoText();

  try
  {
    const response = await resetProgress();
    adoptEconomyFromPlayer( response.player );

    // Keep the current account and environment. Everything else is recreated
    // from the freshly reset server profile on reload.
    const preserved_keys = [
      "player_auth_token",
      "player_id",
      "device_id",
      "runtime_environment_override",
      "server_routing_override",
      "api_base_override",
      "royal_smash_offer_entitlements",
      UI_BACKEND_OVERRIDE_KEY,
    ];
    const preserved = preserved_keys
      .map( key => [ key, localStorage.getItem( key ) ] as const )
      .filter( ( entry ): entry is readonly [ string, string ] => entry[1] !== null );

    localStorage.clear();
    for ( const [ key, value ] of preserved )
      localStorage.setItem( key, value );

    window.location.reload();
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to reset progress on server", error );
    reset_progress_inflight = false;
    updateInfoText();
  }
}

async function resetAdventureOnServer(): Promise<void>
{
  if ( reset_adventure_inflight )
    return;

  if ( !window.confirm( "Reset all Win Streak Tower progress on the server? This cannot be undone." ) )
    return;

  reset_adventure_inflight = true;
  updateInfoText();

  try
  {
    await WinStreakTowerService.resetProgressForDebug();
  }
  catch ( error )
  {
    console.warn( "[DebugConsole] Failed to reset adventure progress on server", error );
  }
  finally
  {
    reset_adventure_inflight = false;
    updateInfoText();
  }
}

function toggleRuntimeEnvironment(): void
{
  const next_key = getNextRuntimeEnvironmentKey();
  const next_label = next_key === RuntimeEnvironmentKey.PROD ? 'PROD' : 'DEV';
  if ( !window.confirm( `Switch server environment to ${next_label} and reload?` ) ) return;

  setRuntimeEnvironmentOverride( next_key );
  window.location.reload();
}

async function checkServerRouting(): Promise<void>
{
  const result = await ServerRoutingService.check( 'manual' );
  console.info( `[DebugConsole] routing ${result.error ? `failed: ${result.error}` : `${result.changed ? "changed" : "unchanged"}: ${result.target ? `${result.target.api_url} (rule ${result.rule_id})` : "home server"}`}` );
  updateInfoText();
}

function clearServerRoutingOverride(): void
{
  if ( !getServerRoutingOverride() ) return;
  if ( !window.confirm( "Drop the server routing override and reload? The home server may route you again on the next check." ) ) return;
  setServerRoutingOverride( null );
  window.location.reload();
}

function createNewUserAndReload(): void
{
  if ( !window.confirm( "Create a new user and reload the app? This clears local progress and auth data." ) ) return;

  const env_override = localStorage.getItem( "runtime_environment_override" );
  const routing_override = localStorage.getItem( "server_routing_override" );
  const ui_backend_override = localStorage.getItem( UI_BACKEND_OVERRIDE_KEY );
  localStorage.clear();
  if ( env_override )
    localStorage.setItem( "runtime_environment_override", env_override );
  if ( routing_override )
    localStorage.setItem( "server_routing_override", routing_override );
  if ( ui_backend_override )
    localStorage.setItem( UI_BACKEND_OVERRIDE_KEY, ui_backend_override );

  resetDeviceIdForNewUser();
  window.location.reload();
}

function buildPasswordPrompt( onSubmit: ( password: string, setError: ( msg: string ) => void ) => Promise<boolean> ): HTMLDivElement
{
  const overlay = document.createElement( "div" );
  overlay.id = "debugPasswordPrompt";
  overlay.style.position = "fixed";
  overlay.style.left = "0";
  overlay.style.top = "0";
  overlay.style.right = "0";
  overlay.style.bottom = "0";
  overlay.style.zIndex = "2147483647";
  overlay.style.background = "rgba(10, 12, 20, 0.94)";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.font = "13px Arial, sans-serif";

  const box = document.createElement( "div" );
  box.style.width = "260px";
  box.style.padding = "18px";
  box.style.borderRadius = "10px";
  box.style.background = "rgba(255,255,255,0.06)";
  box.style.border = "1px solid rgba(255,255,255,0.15)";

  const title = document.createElement( "div" );
  title.textContent = "DEBUG ACCESS";
  title.style.color = "#fff";
  title.style.font = "700 14px Arial, sans-serif";
  title.style.marginBottom = "12px";

  const input = document.createElement( "input" );
  input.type = "password";
  input.placeholder = "Password";
  input.style.width = "100%";
  input.style.boxSizing = "border-box";
  input.style.padding = "10px";
  input.style.borderRadius = "8px";
  input.style.border = "1px solid rgba(255,255,255,0.2)";
  input.style.background = "rgba(255,255,255,0.08)";
  input.style.color = "#fff";
  input.style.font = "14px monospace";
  input.style.marginBottom = "10px";
  input.style.outline = "none";

  const error_el = document.createElement( "div" );
  error_el.style.color = "#f55";
  error_el.style.font = "11px Arial, sans-serif";
  error_el.style.marginBottom = "10px";
  error_el.style.minHeight = "14px";

  const row = document.createElement( "div" );
  row.style.display = "flex";
  row.style.gap = "8px";

  const submit_btn = document.createElement( "button" );
  submit_btn.type = "button";
  submit_btn.textContent = "UNLOCK";
  styleButton( submit_btn, "#385090" );
  submit_btn.style.flex = "1";

  const cancel_btn = document.createElement( "button" );
  cancel_btn.type = "button";
  cancel_btn.textContent = "CANCEL";
  styleButton( cancel_btn, "#555" );
  cancel_btn.addEventListener( "click", () => overlay.remove() );

  const submit = () =>
  {
    error_el.textContent = "";
    submit_btn.disabled = true;
    submit_btn.textContent = "…";
    void onSubmit( input.value, ( msg ) => { error_el.textContent = msg; } )
      .then( ( success ) => { if ( success ) overlay.remove(); } )
      .finally( () =>
      {
        submit_btn.disabled = false;
        submit_btn.textContent = "UNLOCK";
      } );
  };

  submit_btn.addEventListener( "click", submit );
  input.addEventListener( "keydown", ( ev ) => { if ( ev.key === "Enter" ) submit(); } );

  row.appendChild( submit_btn );
  row.appendChild( cancel_btn );

  box.appendChild( title );
  box.appendChild( input );
  box.appendChild( error_el );
  box.appendChild( row );
  overlay.appendChild( box );

  requestAnimationFrame( () => input.focus() );

  return overlay;
}

export const DebugConsoleManager = {
  open(): void
  {
    NetworkManager.setDebugActive( true );
    this.attachEntryButton();

    if ( !panel_el )
    {
      panel_el = buildPanel();
      document.body.appendChild( panel_el );
    }

    updateInfoText();
    panel_el.style.display = "block";
  },

  promptPassword(): void
  {
    if ( document.getElementById( "debugPasswordPrompt" ) ) return;

    const overlay = buildPasswordPrompt( async ( password, setError ) =>
    {
      if ( password === "" )
      {
        setError( "Enter password" );
        return false;
      }

      let response: Response;
      try
      {
        response = await fetch( `${GlobalSettings.SUPPORT_BASE_URL}/auth/verify-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify( { password } ),
        } );
      }
      catch
      {
        setError( "Network error, try again" );
        return false;
      }

      if ( response.ok )
      {
        DebugConsoleManager.open();
        return true;
      }

      if ( response.status === 429 )
      {
        setError( "Too many attempts" );
        return false;
      }

      const data = await response.json().catch( () => ( {} ) ) as { attempts_left?: number };
      setError( data.attempts_left !== undefined
        ? `Wrong password, ${data.attempts_left} attempts left`
        : "Wrong password" );
      return false;
    } );

    document.body.appendChild( overlay );
    ( overlay as any )._input?.focus();
  },

  close(): void
  {
    NetworkManager.setDebugActive( false );
    if ( panel_el )
      panel_el.style.display = "none";
  },

  attachEntryButton(): void
  {
    if ( entry_attached ) return;
    entry_attached = true;

    const button = document.createElement( "button" );
    button.type = "button";
    button.textContent = "DEBUG";
    button.style.position = "fixed";
    button.style.left = "12px";
    button.style.bottom = "100px";
    button.style.zIndex = "2147483647";
    button.style.padding = "10px 14px";
    button.style.borderRadius = "10px";
    button.style.border = "2px solid #000";
    button.style.background = "#ffd200";
    button.style.color = "#000";
    button.style.font = "700 12px Arial, sans-serif";
    button.style.boxShadow = "0 2px 6px rgba(0,0,0,0.35)";
    button.style.cursor = "pointer";

    button.addEventListener( "click", () => DebugConsoleManager.open() );

    document.body.appendChild( button );
  },

  attachSecretCodeListener(): void
  {
    if ( tap_code_attached ) return;
    tap_code_attached = true;

    if ( 'PointerEvent' in window )
    {
      document.addEventListener( "pointerdown", ( ev: PointerEvent ) =>
      {
        if ( ev.isPrimary === false ) return;
        registerTapCodeStep( getTapQuadrant( ev.clientX, ev.clientY ) );
      }, { passive: true } );
      return;
    }

    document.addEventListener( "touchstart", ( ev: TouchEvent ) =>
    {
      const touch = ev.changedTouches?.[ 0 ];
      if ( !touch ) return;
      registerTapCodeStep( getTapQuadrant( touch.clientX, touch.clientY ) );
    }, { passive: true } );
  }
};

/**
 * The win overlay with a coin reward. With `then`, Claim continues the way a real
 * win does (VFX kept across the screen change), so the coin flight into the top
 * counter can be checked on the menu or on the next level.
 */
function openDebugWinOverlay( stars: number, then?: "menu" | "level" ): void
{
  const onContinue = then === "menu"
    ? () => { void ScreenManager.showScreen( Screens.MENU, { preserve_vfx: true } ); }
    : then === "level"
      ? () => { void ScreenManager.showScreen( Screens.GAME, { level: StatisticsStorage.load().level, preserve_vfx: true } ); }
      : undefined;
  OverlayFactory.openOverlay( OverlayId.GAME_RESULT_OVERLAY, {
    title_key: "RESULT.LEVEL_PASSED",
    stars,
    rewards: [ { currency: "coin", amount: 120 } ],
    onContinue,
  } );
}

/** Prize Shot as on the first visit: a fresh board, the free balls, no coin continues bought. */
function resetPrizeShotToStart(): void
{
  PrizeShotService.debugReset();
  // The shelf on screen was built from the old board: show the new one.
  if ( ScreenManager.getCurrentScreenInstance() instanceof PrizeShotScreen )
  {
    DebugConsoleManager.close();
    void ScreenManager.showScreen( Screens.GAME, { prize_shot: true } );
  }
}
